import { useState, useRef, useEffect } from 'react';
import { useDeviceDetail } from '../hooks/useNanoMDM';
import { StatusBadge, AuditList } from '../App';
import { query, RESTRICTIONS_PROFILE_ID } from '../api';
import { dmy, dmyDate, dmyTime, DateInput } from '../dates';
import { calcMonthly } from '../lib/installment';
import { rentTotal, rentPerPeriod, rentEndDate, rentPeriodsElapsed, UNIT_LABEL, WEEKDAYS, weekdayLabel } from '../lib/rental';
import { appleModelName } from '../lib/appleModels';
import DevicePhoto from './DevicePhoto';
import NotifyModal from './NotifyModal';
import { ImageGallery, PendingImagePicker, Lightbox, IMAGE_CSS } from './ImageUploader';
import { confirmVN, promptVN, alertVN } from './Dialog';

const TABS = ['Thông tin', 'Khách hàng', 'Thanh toán', 'Vị trí', 'Ứng dụng', 'Profiles', 'Lịch sử lệnh', 'Nhật ký', 'Chủ trước'];
const SUP_HINT = 'Cần máy giám sát (DEP/ABM) — lệnh này không chạy trên máy user-approved';

export default function DeviceModal({ device, role, alCodeAccess = false, locations = [], onClose, onAction, onReload, showToast, isPending }) {
  const [tab, setTab]                   = useState('Thông tin');
  const [confirm, setConfirm]           = useState(null);  // {action:'clear', step:1|2} — xác nhận 2 bước
  const [owner, setOwner]               = useState(null);
  const [busyAct, setBusyAct]           = useState(null); // lệnh nhanh đang gửi (hiện "đang gửi")
  const [notifyOpen, setNotifyOpen]     = useState(false); // modal gửi thông báo lên màn hình
  // Cửa hàng của máy có được bật "cho thuê máy" không (server cũng kiểm tra lại khi lưu).
  const rentalAllowed = !!(locations || []).find(l => l.id === device.location_id && l.rental_enabled);
  const [lostBusy, setLostBusy]         = useState(null); // 'on'/'off' khi đang đổi Lost Mode + chờ máy xác nhận
  const [syncBusy, setSyncBusy]         = useState(false); // đang hỏi DeviceInformation + chờ máy báo về
  const [restrictBusy, setRestrictBusy] = useState(null);  // 'block' khi đang gán profile Hạn chế
  const [orgLock, setOrgLock]           = useState(null);  // { bypass_code, at, ok } — mã Organization Lock (ok = Apple đã xác nhận)
  const [orgBusy, setOrgBusy]           = useState(false); // đang bật Organization Lock
  const [clearBusy, setClearBusy]       = useState(false); // đang chạy "Gỡ MDM (giữ dữ liệu)"
  const [clearResult, setClearResult]   = useState(null);  // { ok, steps:[...] } kết quả Gỡ MDM
  const [khoId, setKhoId]               = useState('');    // kho đích khi "Trả về kho" (đổi máy)
  const [khoBusy, setKhoBusy]           = useState(false);
  const [finalizeBusy, setFinalizeBusy] = useState(false); // đang Hoàn tất / Khôi phục
  const [override, setOverride]         = useState({});   // đè field động (lost_mode) lên device khi đã xác nhận
  const { info, apps, profiles, commands, loading: detailLoading, refresh, reload } = useDeviceDetail(device.udid);
  const canManage = role !== 'staff';
  const isAdmin = role === 'admin';                       // mã org-lock: admin luôn xem + BẬT
  const isOps = role === 'admin' || role === 'operator';  // xóa thanh toán: điều hành+admin
  // canViewAL: được XEM mã Activation Lock. admin luôn được; operator chỉ khi được cấp quyền.
  const canViewAL = isAdmin || (role === 'operator' && alCodeAccess);
  const d = { ...device, ...override };                   // thiết bị + trạng thái mới nhất đã xác nhận
  const profileId = (p) => p.PayloadIdentifier || p.identifier || '';
  const restrictionsAssigned = profiles.some(p => profileId(p) === RESTRICTIONS_PROFILE_ID); // đã gán profile Hạn chế?
  const icloudInstalled = profiles.some(p => profileId(p) === 'com.odtravel.icloudblock'); // profile ẩn iCloud ĐÃ có trên máy?

  // Sau khi GÁN/GỠ profile: hỏi lại ProfileList rồi đọc lại nhiều lần để danh sách
  // tự cập nhật (máy trả kết quả bất đồng bộ sau vài giây).
  async function reloadProfilesSoon() {
    await refresh().catch(() => {}); // enqueue ProfileList (+ đọc 1 lần)
    for (let i = 0; i < 6; i++) {    // đọc lại ~18s để bắt kết quả máy báo về
      await new Promise(r => setTimeout(r, 3000));
      await reload().catch(() => {});
    }
  }
  const modelCommercial = appleModelName(device.product, device.model); // "iPhone X" từ iPhone10,3
  const sup = d.is_supervised === false; // true = chắc chắn CHƯA giám sát -> mờ lệnh supervised-only
  const mdmGone = d.enabled === false;   // đã CheckOut / gỡ MDM -> mọi lệnh vô nghĩa, ẩn hết
  const inKho = d.in_warehouse === true;  // đang TRONG KHO -> chỉ xem, ẩn mọi thao tác

  const [icloudOn, setIcloudOn] = useState(null);   // ẩn iCloud cá nhân: null=đang tải
  const [icloudBusy, setIcloudBusy] = useState(false);

  useEffect(() => {
    setOverride({}); // đổi máy -> bỏ override cũ
    setOrgLock(null);
    setIcloudOn(null);
    if (device.udid) {
      query.getDeviceOwner(device.udid).then(setOwner).catch(() => {});
      if (canManage) {
        // manager+ xem TRẠNG THÁI org-lock (on/off); admin thêm cả MÃ bypass (backend redact).
        query.getOrgLock(device.udid).then(setOrgLock).catch(() => {});
      }
      if (canManage) {
        query.getICloudBlock(device.udid).then(r => setIcloudOn(!!(r && r.on))).catch(() => {});
      }
    }
  }, [device.udid]);

  async function toggleICloud(on) {
    setIcloudBusy(true);
    try {
      await query.setICloudBlock(device.udid, on);
      setIcloudOn(on);
      showToast && showToast(on ? 'Đã bật ẩn iCloud cá nhân — gửi lệnh tới máy' : 'Đã tắt ẩn iCloud cá nhân', 'success', on ? '🔒' : '🔓');
    } catch (ex) { showToast && showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setIcloudBusy(false); }
  }

  // Bật Organization Activation Lock: MDM tự sinh mã + gọi Apple (DEP) khóa server-side.
  // Không cần Apple ID nào; trả mã + phản hồi Apple để đối chiếu SUCCESS.
  async function enableOrgLockNow() {
    if (!(await confirmVN('Sau khi erase, máy sẽ đòi mã bypass để kích hoạt. Đảm bảo đã lưu/backup mã.', { title: 'Bật Organization Lock cho máy này?', okText: 'Bật' }))) return;
    setOrgBusy(true);
    try {
      const res = await query.enableOrgLock(device.udid).catch(e => ({ error: e }));
      if (!res || res.error) {
        showToast && showToast('Bật Organization Lock lỗi' + (res && res.error ? ': ' + (res.error.message || '') : ''), 'error', '❌');
        return;
      }
      // applied = lần này Apple KHÓA được không. Khi FAILED, backend trả về mã ĐANG LƯU
      // (mã tốt) — KHÔNG ghi đè bằng mã ma, nên hiển thị giữ nguyên mã dùng được.
      const applied = !!res.applied;
      if (res.bypass_code) setOrgLock({ bypass_code: res.bypass_code, at: '', ok: !!res.ok, on: true });
      if (applied) {
        showToast && showToast('Đã bật Organization Lock', 'success', '🔒');
      } else {
        console.warn('org-lock apple_response:', res.apple_response);
        showToast && showToast(`Không bật lại được (Apple: ${res.dep_status}) — máy có thể đã khóa sẵn; giữ mã hiện có`, 'info', '⚠️');
      }
    } finally {
      setOrgBusy(false);
    }
  }

  // Lệnh nhanh (không có trạng thái để poll): hiện nhãn "đang gửi" trong lúc enqueue.
  async function act(action, opts = {}) {
    setBusyAct(action);
    try { return await onAction(action, device.udid, opts); }
    finally { setBusyAct(null); }
  }
  // Lệnh nguồn cần XÁC NHẬN trước khi gửi (tắt nguồn / khởi động lại).
  async function actConfirm(action, { title, message, okText }) {
    if (!(await confirmVN(message, { title, okText, danger: true }))) return;
    await act(action);
  }

  // Bật/Tắt Lost Mode giống định vị: gửi lệnh -> yêu cầu DeviceInformation ->
  // poll tới khi máy báo IsMDMLostModeEnabled đúng chiều -> cập nhật UI.
  async function toggleLostMode(on, opts = {}) {
    setLostBusy(on ? 'on' : 'off');
    try {
      const r = await onAction(on ? 'lost_mode_on' : 'lost_mode_off', device.udid, opts);
      if (r && r.ok === false) return; // lỗi gửi -> App đã toast
      await refresh().catch(() => {}); // enqueue DeviceInformation để lấy trạng thái mới
      for (let i = 0; i < 12; i++) {   // chờ máy báo về (tối đa ~48s)
        await new Promise(res => setTimeout(res, 4000));
        const fresh = await query.getDevice(device.udid).catch(() => null);
        if (fresh && fresh.lost_mode === on) {
          setOverride(o => ({ ...o, lost_mode: on }));
          onReload && onReload();
          showToast && showToast(on ? 'Máy đã bị khóa' : 'Máy đã mở khóa', 'success', on ? '📍' : '🔓');
          return;
        }
      }
      // Hết thời gian chờ (máy offline): cập nhật lạc quan + nhắc chưa xác nhận.
      setOverride(o => ({ ...o, lost_mode: on }));
      onReload && onReload();
      showToast && showToast('Đã gửi — máy chưa báo về, sẽ tự cập nhật khi online', 'info', '⏳');
    } finally {
      setLostBusy(null);
    }
  }

  // Đồng bộ trạng thái: chỉ hỏi DeviceInformation (KHÔNG gửi lệnh khóa/mở) rồi poll
  // tới khi máy báo về (last_seen đổi) -> cập nhật lost_mode/is_supervised theo máy thật.
  // Dùng khi DB lệch với máy (vd: đã tắt Lost Mode ngoài hệ nhưng chưa có báo cáo mới).
  async function syncState() {
    setSyncBusy(true);
    try {
      const prevSeen = device.last_seen;
      await refresh().catch(() => {}); // enqueue DeviceInformation (kèm IsMDMLostModeEnabled, IsSupervised)
      for (let i = 0; i < 12; i++) {   // chờ máy báo về (tối đa ~48s)
        await new Promise(res => setTimeout(res, 4000));
        const fresh = await query.getDevice(device.udid).catch(() => null);
        if (fresh && fresh.last_seen !== prevSeen) { // máy đã liên lạc lại -> đã có báo cáo mới
          setOverride(o => ({ ...o, lost_mode: fresh.lost_mode, is_supervised: fresh.is_supervised }));
          onReload && onReload();
          showToast && showToast('Đã đồng bộ trạng thái từ máy', 'success', '🔄');
          return;
        }
      }
      showToast && showToast('Máy chưa phản hồi — sẽ tự cập nhật khi online', 'info', '⏳');
    } finally {
      setSyncBusy(false);
    }
  }

  // Gán profile Hạn chế — admin chủ động. Gửi InstallProfile rồi poll command_results
  // tới khi Acknowledged. Profile chặn iCloud/Find My/định vị… (Org Lock lo khoá kích hoạt).
  async function assignRestrictions() {
    setRestrictBusy('block');
    try {
      const res = await query.restrictDevice(device.udid).catch(e => ({ error: e }));
      if (!res || !res.command_uuid) {
        showToast && showToast('Gửi lệnh gán lỗi', 'error', '❌');
        return;
      }
      for (let i = 0; i < 15; i++) { // chờ máy xác nhận (~60s)
        await new Promise(r => setTimeout(r, 4000));
        const cmds = await query.getCommandResults(device.udid, { limit: 50 }).catch(() => null);
        const r = cmds && cmds.find(c => c.command_uuid === res.command_uuid);
        if (r && r.status === 'Acknowledged') {
          showToast && showToast('Đã gán profile Hạn chế', 'success', '🔒');
          reloadProfilesSoon(); // tự cập nhật tab Profiles
          return;
        }
        if (r && r.status && r.status.includes('Error')) {
          showToast && showToast('Máy từ chối profile: ' + r.status, 'error', '❌');
          return;
        }
        // NotNow / chưa có kết quả -> tiếp tục chờ
      }
      showToast && showToast('Đã gửi — máy chưa xác nhận, sẽ áp khi online', 'info', '⏳');
    } finally {
      setRestrictBusy(null);
    }
  }

  // NGUY HIỂM: Gỡ MDM (giữ dữ liệu). Backend gửi RemoveProfile -> máy CheckOut. Dữ liệu
  // GIỮ nguyên. Activation Lock KHÔNG tự gỡ (mở khoá bằng nhập tay mã bypass).
  // Sau khi gửi lệnh gỡ MDM, máy CheckOut (enabled=false) sau vài giây khi xử lý
  // RemoveProfile. Poll danh sách để modal tự hiện "đã gỡ MDM" khi máy xác nhận
  // (re-sync selectedDevice ở App.jsx cập nhật prop device -> mdmGone bật).
  function pollUnenroll() {
    (async () => {
      for (let i = 0; i < 8; i++) {
        await new Promise(r => setTimeout(r, 4000));
        onReload && onReload();
      }
    })();
  }

  async function clearOrglock() {
    setClearBusy(true);
    setClearResult(null);
    try {
      const res = await query.clearOrglock(device.udid).catch(e => ({ error: e }));
      if (res && res.error) {
        showToast && showToast((res.error.body && res.error.body.error) || res.error.message || 'Gỡ MDM lỗi', 'error', '❌');
        return;
      }
      setClearResult(res);
      onReload && onReload();
      if (res.ok) {
        showToast && showToast('Đã gỡ MDM (giữ dữ liệu)', 'success', '🔓');
        pollUnenroll();
      } else {
        const failed = (res.steps || []).filter(s => !s.ok);
        const msg = failed.length
          ? `Chưa hoàn tất: ${failed.map(s => s.label).join(', ')} — xem chi tiết bên dưới`
          : 'Có bước chưa hoàn tất — xem chi tiết bên dưới';
        showToast && showToast(msg, 'error', '❌');
      }
    } finally {
      setClearBusy(false);
    }
  }

  // Đổi máy: TRẢ VỀ KHO — snapshot hồ sơ khách (Chủ trước) + xoá data sống + chuyển
  // location về kho đã chọn. Máy thành hàng tồn sẵn sàng cho khách mới.
  async function returnToKhoNow() {
    if (!khoId) { showToast && showToast('Chọn kho để trả máy về', 'error', '❌'); return; }
    if (!(await confirmVN('Hồ sơ khách hiện tại sẽ được LƯU vào "Chủ trước" rồi XOÁ khỏi máy; máy chuyển về kho. Dùng khi khách ĐỔI MÁY.', { title: 'Trả máy về kho?', okText: 'Trả về kho' }))) return;
    setKhoBusy(true);
    try {
      await query.returnToKho(device.udid, Number(khoId));
      showToast && showToast('Đã trả máy về kho (hồ sơ cũ lưu ở Chủ trước)', 'success', '📦');
      onReload && onReload();
      setOwner(null);
    } catch (ex) { showToast && showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setKhoBusy(false); }
  }

  // Hoàn tất: máy đã gỡ MDM -> đưa vào LỊCH SỬ máy đã từng quản lý (ẩn khỏi danh sách chính).
  async function finalizeNow() {
    if (!(await confirmVN('Máy sẽ được chuyển vào "Lịch sử máy đã quản lý" và ẩn khỏi danh sách Thiết bị. Dùng khi đã xử lý xong máy đã gỡ MDM.', { title: 'Hoàn tất máy này?', okText: 'Hoàn tất' }))) return;
    setFinalizeBusy(true);
    try {
      await query.finalizeDevice(device.udid);
      showToast && showToast('Đã hoàn tất — đưa vào lịch sử máy đã quản lý', 'success', '✅');
      onReload && onReload();
      onClose && onClose();
    } catch (ex) { showToast && showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setFinalizeBusy(false); }
  }
  // Khôi phục: đưa máy từ lịch sử trở lại danh sách chính.
  async function unfinalizeNow() {
    setFinalizeBusy(true);
    try {
      await query.unfinalizeDevice(device.udid);
      showToast && showToast('Đã khôi phục — máy trở lại danh sách Thiết bị', 'success', '↩️');
      onReload && onReload();
      onClose && onClose();
    } catch (ex) { showToast && showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setFinalizeBusy(false); }
  }

  async function assignLocation(locationId) {
    try {
      await query.setDeviceLocation(device.udid, locationId);
      showToast && showToast('Đã cập nhật cửa hàng', 'success', '🏬');
      onReload && onReload();
    } catch (ex) { showToast && showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }

  // Danh sách nhân viên (staff) để gán "người cài đặt" — staff chỉ thấy máy mình được gán.
  const [staffList, setStaffList] = useState([]);
  useEffect(() => {
    if (!canManage) return;
    query.listUsers()
      .then(us => setStaffList((us || []).filter(x => x.role === 'staff' && (!device.location_id || x.location_id === device.location_id))))
      .catch(() => {});
  }, [canManage, device.location_id]);
  async function assignInstaller(userId) {
    try {
      await query.setDeviceInstaller(device.udid, userId);
      showToast && showToast(userId ? 'Đã gán người cài đặt' : 'Đã bỏ gán người cài đặt', 'success', '👤');
      onReload && onReload();
    } catch (ex) { showToast && showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }

  async function renameDevice() {
    const name = await promptVN('Tên thiết bị mới:', device.name, { title: 'Đổi tên thiết bị', okText: 'Đổi tên' });
    if (name && name.trim()) act('rename', { name: name.trim() });
  }

  async function osUpdate() {
    const version = await promptVN('Phiên bản iOS muốn cập nhật (ví dụ 17.5.1):', '', { title: 'Cập nhật iOS', okText: 'Cập nhật' });
    if (version && version.trim()) act('os_update', { version: version.trim() });
  }

  async function lostModeOn() {
    const def = (owner && owner.lost_message) || 'Thiết bị này thuộc quyền quản lý của công ty.';
    const message = await promptVN('Lời nhắn hiển thị trên màn hình khi khóa điện thoại:', def, { title: 'Khóa điện thoại', okText: 'Khóa' });
    if (message !== null) toggleLostMode(true, { message: message || undefined });
  }

  return (
    <div className="overlay dev-drawer open" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="modal-head">
          <div className="modal-title-wrap">
            <DevicePhoto product={device.product} className="modal-icon" fallback={<div className="modal-icon">📱</div>} />
            <div>
              <div className="modal-device-name">{modelCommercial ? `${modelCommercial} | ${device.name}` : device.name}</div>
              <div className="modal-device-sub mono">{device.serial} · {device.platform} · {device.os}</div>
            </div>
            <StatusBadge status={device.status} />
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Gán cửa hàng (manager trở lên) */}
        {canManage && (
          <div className="assign-bar">
            <span className="action-group-label">Cửa hàng</span>
            <select className="assign-select" value={device.location_id || ''} onChange={e => assignLocation(Number(e.target.value) || 0)}>
              <option value="">— Chưa gán —</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        )}
        {canManage && (
          <div className="assign-bar">
            <span className="action-group-label">Người cài đặt</span>
            <select className="assign-select" value=""
              onChange={e => { const v = e.target.value; if (v === '') return; assignInstaller(v === '__none__' ? 0 : Number(v)); }}>
              <option value="">{device.enrolled_by ? `Hiện tại: ${device.enrolled_by} — đổi…` : '— Gán nhân viên cài đặt —'}</option>
              {staffList.map(u => <option key={u.id} value={u.id}>{u.full_name ? `${u.full_name} (${u.username})` : u.username}</option>)}
              {device.enrolled_by && <option value="__none__">— Bỏ gán —</option>}
            </select>
          </div>
        )}

        {/* Action bar — chỉ các lệnh MDM THẬT (staff chỉ thấy nhóm an toàn) */}
        {/* Máy đã gỡ MDM: ẩn toàn bộ lệnh (gửi sẽ không tới máy), chỉ hiện thông báo. */}
        <div className="action-bar">
          {mdmGone ? (
          <>
            <div className="sup-warn">
              ⚠️ Máy <b>đã gỡ khỏi OD Travel MDM</b> (CheckOut). Các lệnh MDM đã được ẩn vì máy không còn kết nối — gửi lệnh sẽ không tới máy. Cần <b>enroll lại</b> để quản lý.
            </div>
            {/* Hoàn tất -> đưa vào lịch sử máy đã quản lý; hoặc Khôi phục nếu đang ở lịch sử. manager+ */}
            {canManage && (
              <ActionGroup label="Kết thúc quản lý">
                {d.finalized ? (
                  <>
                    <span style={{ fontSize: 13, color: 'var(--text2)' }}>
                      📁 Máy đang ở <b>Lịch sử máy đã quản lý</b>{d.finalized_at ? ` · hoàn tất ${d.finalized_at}` : ''}{d.finalized_by ? ` · bởi ${d.finalized_by}` : ''}.
                    </span>
                    <ActBtn loading={finalizeBusy} loadingText="⏳…" onClick={unfinalizeNow}>↩️ Khôi phục về danh sách</ActBtn>
                  </>
                ) : (
                  <>
                    <ActBtn primary loading={finalizeBusy} loadingText="⏳ Đang hoàn tất…"
                      title="Đưa máy đã gỡ MDM vào Lịch sử máy đã quản lý — ẩn khỏi danh sách Thiết bị"
                      onClick={finalizeNow}>✅ Hoàn tất (đưa vào lịch sử)</ActBtn>
                    <div style={{ flexBasis: '100%', fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                      Máy sẽ được lưu vào <b>Lịch sử máy đã quản lý</b> và ẩn khỏi danh sách Thiết bị. Nếu máy enroll lại sẽ tự quay về.
                    </div>
                  </>
                )}
              </ActionGroup>
            )}
            {/* Vẫn hiện mã Organization Lock — máy đã gỡ MDM vẫn có thể bị khóa kích hoạt,
                đây là chìa khoá mở khi erase. Chỉ admin / operator được cấp quyền thấy. */}
            {canViewAL && orgLock && orgLock.bypass_code && (
              <ActionGroup label="🔑 Mã Organization Lock (mở khóa khi máy bị khóa kích hoạt)">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <code style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 15, letterSpacing: 1,
                    background: orgLock.ok ? 'var(--green-bg)' : 'var(--red-bg, rgba(239,68,68,.12))',
                    color: orgLock.ok ? 'var(--green)' : 'var(--red)', padding: '5px 10px', borderRadius: 6, userSelect: 'all' }}>{orgLock.bypass_code}</code>
                  {orgLock.ok
                    ? <span style={{ color: 'var(--text3)', fontSize: 12 }}>✅ đã khóa · <b>DÙNG 1 LẦN</b> · LƯU KỸ{orgLock.at ? ` · kích hoạt ${dmyTime(orgLock.at)}` : ''}</span>
                    : <span style={{ color: 'var(--red)', fontSize: 12, fontWeight: 600 }}>⚠️ CHƯA xác nhận với Apple — mã có thể KHÔNG mở được</span>}
                </span>
              </ActionGroup>
            )}
            {canManage && !canViewAL && (
              <ActionGroup label="Activation Lock — Organization">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                  {orgLock == null
                    ? <span style={{ color: 'var(--text3)' }}>… đang tải trạng thái</span>
                    : orgLock.on
                      ? (orgLock.ok
                        ? <b style={{ color: 'var(--green)' }}>🔒 ĐANG BẬT (đã xác nhận với Apple)</b>
                        : <b style={{ color: 'var(--warn, #b8860b)' }}>⚠️ Đã bật nhưng CHƯA xác nhận với Apple</b>)
                      : <b style={{ color: 'var(--text3)' }}>🔓 CHƯA bật</b>}
                </span>
                <div style={{ flexBasis: '100%', fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                  Chỉ <b>admin</b> (hoặc điều hành được cấp quyền) mới xem <b>mã bypass</b> — bạn chỉ xem trạng thái.
                </div>
              </ActionGroup>
            )}
          </>
          ) : inKho ? (
          <>
            <div className="sup-warn">
              📦 Máy đang <b>TRONG KHO</b> ({d.location_name || 'kho'}) — chỉ <b>xem thông tin</b>. Vào trang <b>Kho</b> bấm <b>“Kéo về cửa hàng”</b> rồi mới nhập khách / gửi lệnh.
              {isOps && <> Điều hành/admin có thể <b>check-in, yêu cầu báo cáo, đồng bộ</b> để kiểm tra máy.</>}
            </div>
            {isOps && (
              <ActionGroup label="Truy vấn (máy trong kho)">
                <ActBtn primary loading={busyAct === 'checkin'} onClick={() => act('checkin')}>🔄 Check-in</ActBtn>
                <ActBtn onClick={refresh}>📥 Yêu cầu báo cáo</ActBtn>
                <ActBtn loading={syncBusy} loadingText="⏳ Đang đồng bộ…"
                  title="Hỏi máy trạng thái thật rồi cập nhật lại"
                  onClick={syncState}>🔄 Đồng bộ trạng thái</ActBtn>
              </ActionGroup>
            )}
          </>
          ) : (
          <>
          {d.lost_mode === true && (
            <div className="lost-banner">📍 Máy <b>ĐANG bị khóa</b> (khóa màn hình + hiện lời nhắn). Bấm <b>Tắt</b> ở nhóm Khóa điện thoại để mở.</div>
          )}
          {d.is_supervised === false && (
            <div className="sup-warn">
              ⚠️ Máy <b>chưa giám sát</b> (enroll kiểu user-approved). Các lệnh cần <b>supervised</b> (Âm thanh, Khởi động lại, Tắt nguồn, Đổi tên, Khóa điện thoại) đã bị mờ — sẽ báo lỗi nếu gửi. Cần enroll qua <b>DEP/ABM</b> để dùng.
            </div>
          )}
          <ActionGroup label="Truy vấn">
            <ActBtn primary loading={busyAct === 'checkin'} onClick={() => act('checkin')}>🔄 Check-in</ActBtn>
            {/* Nhân viên (staff) CHỈ được Check-in — ẩn truy vấn/đồng bộ/âm thanh */}
            {canManage && <ActBtn onClick={refresh}>📥 Yêu cầu báo cáo</ActBtn>}
            {canManage && (
              <ActBtn loading={syncBusy} loadingText="⏳ Đang đồng bộ…"
                title="Hỏi máy trạng thái thật (khóa, giám sát…) và cập nhật lại — dùng khi hiển thị lệch với máy"
                onClick={syncState}>🔄 Đồng bộ trạng thái</ActBtn>
            )}
            {canManage && <ActBtn disabled={sup} loading={busyAct === 'sound'} title={SUP_HINT} onClick={() => act('sound')}>🔊 Âm thanh</ActBtn>}
          </ActionGroup>
          {/* Thông báo = đặt ẢNH NỀN cả màn khoá + màn chính (MDM không có banner). */}
          {canManage && (
            <ActionGroup label="Thông báo · cần giám sát">
              <ActBtn disabled={sup} title={SUP_HINT} onClick={() => setNotifyOpen(true)}>📢 Gửi thông báo</ActBtn>
            </ActionGroup>
          )}
          {/* Nút "Khóa" (DeviceLock) đã bỏ: iPhone mở lại bằng passcode nên vô dụng
              theo hợp đồng. Dùng Lost Mode (bên dưới) để khóa thật. */}
          {canManage && (
            <ActionGroup label="Nguồn · cần giám sát">
              <ActBtn disabled={sup} loading={busyAct === 'restart'} title={SUP_HINT} onClick={() => actConfirm('restart', { title: 'Khởi động lại máy?', message: 'Máy sẽ KHỞI ĐỘNG LẠI ngay. Nếu máy đang có passcode, sau khi khởi động sẽ cần nhập lại. Tiếp tục?', okText: 'Khởi động lại' })}>🔄 Khởi động lại</ActBtn>
              <ActBtn disabled={sup} loading={busyAct === 'shutdown'} title={SUP_HINT} onClick={() => actConfirm('shutdown', { title: 'Tắt nguồn máy?', message: 'Máy sẽ TẮT NGUỒN. Sau đó phải bật lại BẰNG TAY tại máy — KHÔNG thể bật từ xa. Tiếp tục?', okText: 'Tắt nguồn' })}>⏻ Tắt nguồn</ActBtn>
            </ActionGroup>
          )}
          {canManage && (
            <ActionGroup label="Hệ thống">
              <ActBtn disabled={sup} loading={busyAct === 'rename'} title={SUP_HINT} onClick={renameDevice}>✏️ Đổi tên</ActBtn>
              <ActBtn loading={busyAct === 'os_update'} onClick={osUpdate}>⬆️ Cập nhật iOS</ActBtn>
            </ActionGroup>
          )}
          {canManage && (
            <ActionGroup label="Hạn chế · cần giám sát">
              <ActBtn disabled={sup || !!restrictBusy} loading={restrictBusy === 'block'} loadingText="⏳ Đang gán…"
                title={sup ? SUP_HINT : 'Gán profile CHẶN: cấm reset/đổi tên/VPN/cài profile… (KHÔNG chặn eSIM, mạng di động, AirDrop)'}
                onClick={() => assignRestrictions()}>🔒 Gán profile Hạn chế{restrictionsAssigned ? ' ✅' : ''}</ActBtn>
            </ActionGroup>
          )}
          {canManage && (
            <ActionGroup label={`Ẩn iCloud cá nhân của khách${
              sup ? ' · máy CHƯA giám sát — KHÔNG có tác dụng'
                : icloudOn === true ? (icloudInstalled ? ' · ĐANG BẬT (đã áp trên máy)' : ' · BẬT — chưa thấy trên máy')
                  : icloudOn === false ? ' · đang tắt' : ''
              }`}>
              <ActBtn disabled={sup || icloudBusy || icloudOn === true} loading={icloudBusy && icloudOn !== true} loadingText="⏳…"
                title={sup ? SUP_HINT : 'Chặn khách thêm/dùng iCloud cá nhân + Find My (tránh khoá kích hoạt gắn Apple ID khách)'}
                onClick={() => toggleICloud(true)}>🔒 Bật ẩn iCloud</ActBtn>
              <ActBtn disabled={icloudBusy || icloudOn === false} loading={icloudBusy && icloudOn === true} loadingText="⏳…"
                title="Gỡ chặn — cho khách dùng iCloud cá nhân lại (dùng khi trả máy cho khách)"
                onClick={() => toggleICloud(false)}>🔓 Tắt (cho dùng iCloud)</ActBtn>
              {sup && (
                <div style={{ flexBasis: '100%', fontSize: 12, color: 'var(--danger)', marginTop: 2 }}>
                  ⚠️ Máy <b>chưa giám sát (supervised)</b> → hạn chế / ẩn iCloud <b>không có tác dụng</b> dù hệ thống báo Bật. Muốn khoá được iCloud khách, máy phải enroll qua <b>ADE/ABM</b> (giám sát).
                </div>
              )}
              {!sup && icloudOn === true && !icloudInstalled && (
                <div style={{ flexBasis: '100%', fontSize: 12, color: 'var(--warn, #b8860b)', marginTop: 2 }}>
                  ⚠️ Hệ thống đã Bật nhưng <b>chưa thấy profile ẩn iCloud trên máy</b> (máy có thể đang offline / chưa nhận lệnh). Bấm <b>“Yêu cầu danh sách”</b> ở tab Profiles để cập nhật, hoặc bấm <b>Bật</b> lại để gửi lại lệnh.
                </div>
              )}
              <div style={{ flexBasis: '100%', fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                Chặn khách đăng nhập iCloud/Find My cá nhân (tránh máy bị khoá kích hoạt theo Apple ID khách mà mình không mở được). Chỉ hiệu lực trên máy <b>giám sát</b>. Trạng thái hiển thị dựa trên danh sách profile máy báo về.
              </div>
            </ActionGroup>
          )}
          {canManage && !canViewAL && (
            <ActionGroup label="Activation Lock — Organization">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                {orgLock == null
                  ? <span style={{ color: 'var(--text3)' }}>… đang tải trạng thái</span>
                  : orgLock.on
                    ? (orgLock.ok
                      ? <b style={{ color: 'var(--green)' }}>🔒 ĐANG BẬT (đã xác nhận với Apple)</b>
                      : <b style={{ color: 'var(--warn, #b8860b)' }}>⚠️ Đã bật nhưng CHƯA xác nhận với Apple</b>)
                    : <b style={{ color: 'var(--text3)' }}>🔓 CHƯA bật</b>}
              </span>
              <div style={{ flexBasis: '100%', fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                Khóa kích hoạt server-side bảo vệ máy sau khi erase. Chỉ <b>admin</b> (hoặc điều hành được cấp quyền) mới xem <b>mã bypass</b> — bạn chỉ xem trạng thái bật/tắt.
              </div>
            </ActionGroup>
          )}
          {/* Điều hành ĐƯỢC CẤP QUYỀN: chỉ XEM mã bypass (không có nút Bật — bật là quyền admin). */}
          {canViewAL && !isAdmin && (
            <ActionGroup label="🔑 Mã Organization Lock (Activation Lock)">
              {orgLock == null
                ? <span style={{ color: 'var(--text3)', fontSize: 14 }}>… đang tải trạng thái</span>
                : orgLock.bypass_code
                  ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <code style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 15, letterSpacing: 1,
                        background: orgLock.ok ? 'var(--green-bg)' : 'var(--red-bg, rgba(239,68,68,.12))',
                        color: orgLock.ok ? 'var(--green)' : 'var(--red)', padding: '5px 10px', borderRadius: 6, userSelect: 'all' }}>{orgLock.bypass_code}</code>
                      {orgLock.ok
                        ? <span style={{ color: 'var(--text3)', fontSize: 12 }}>✅ đã khóa · <b>DÙNG 1 LẦN</b> · LƯU KỸ{orgLock.at ? ` · kích hoạt ${dmyTime(orgLock.at)}` : ''}</span>
                        : <span style={{ color: 'var(--red)', fontSize: 12, fontWeight: 600 }}>⚠️ CHƯA xác nhận với Apple — mã có thể KHÔNG mở được</span>}
                    </span>
                  : <b style={{ color: 'var(--text3)', fontSize: 14 }}>🔓 CHƯA bật Organization Lock</b>}
              <div style={{ flexBasis: '100%', fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                Bạn được cấp quyền <b>xem mã</b> để mở khóa kích hoạt. Việc <b>bật/tạo mã mới</b> là quyền của admin.
              </div>
            </ActionGroup>
          )}
          {isAdmin && (
            <ActionGroup label="Activation Lock — Organization (server-side, không cần Apple ID)">
              <ActBtn disabled={orgBusy} loading={orgBusy} loadingText="⏳ Đang bật…"
                title="MDM tự sinh mã + gọi Apple (DEP) khóa Organization Activation Lock. Erase xong máy đòi mã này để kích hoạt. Không cần Apple ID nào. Mã DÙNG 1 LẦN — bấm lại để tạo mã mới trước mỗi lần khóa."
                onClick={enableOrgLockNow}>🔒 {orgLock && orgLock.bypass_code ? 'Bật lại (tạo mã mới)' : 'Bật Organization Lock'}</ActBtn>
              {orgLock && orgLock.bypass_code && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 4 }}>
                  <code style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 15, letterSpacing: 1,
                    background: orgLock.ok ? 'var(--green-bg)' : 'var(--red-bg, rgba(239,68,68,.12))',
                    color: orgLock.ok ? 'var(--green)' : 'var(--red)', padding: '5px 10px', borderRadius: 6, userSelect: 'all' }}>{orgLock.bypass_code}</code>
                  {orgLock.ok
                    ? <span style={{ color: 'var(--text3)', fontSize: 12 }}>✅ đã khóa · dùng được · <b>DÙNG 1 LẦN</b> · LƯU KỸ{orgLock.at ? ` · kích hoạt ${dmyTime(orgLock.at)}` : ''}</span>
                    : <span style={{ color: 'var(--red)', fontSize: 12, fontWeight: 600 }}>⚠️ CHƯA xác nhận với Apple — mã có thể KHÔNG mở được</span>}
                </span>
              )}
              {orgLock && orgLock.bypass_code && !orgLock.ok && (
                <div style={{ flexBasis: '100%', fontSize: 12, color: 'var(--red)', marginTop: 2 }}>
                  Lệnh bật chưa tới Apple (thường do token DEP lỗi). <b>Kiểm tra token DEP</b> rồi bấm lại <b>"Bật lại (tạo mã mới)"</b> tới khi hiện <b>✅ đã khóa</b> mới dùng mã.
                </div>
              )}
              <div style={{ flexBasis: '100%', fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                ⚠️ Mã bypass dùng <b>một lần</b>: mở khóa xong mã cũ hết hiệu lực. Muốn khóa/mở lần nữa, bấm <b>"Bật lại (tạo mã mới)"</b> rồi dùng mã mới.
              </div>
            </ActionGroup>
          )}
          {canManage && (
            <ActionGroup label="Khóa điện thoại">
              <ActBtn disabled={sup || d.lost_mode === true || lostBusy !== null}
                loading={lostBusy === 'on'} loadingText="⏳ Đang bật…"
                title={sup ? SUP_HINT : (d.lost_mode === true ? 'Máy đang bị khóa' : '')}
                onClick={lostModeOn}>📍 Bật</ActBtn>
              <ActBtn disabled={sup || d.lost_mode === false || lostBusy !== null}
                loading={lostBusy === 'off'} loadingText="⏳ Đang tắt…"
                title={sup ? SUP_HINT : (d.lost_mode === false ? 'Máy không bị khóa' : '')}
                onClick={() => toggleLostMode(false)}>✅ Tắt</ActBtn>
            </ActionGroup>
          )}
          {canManage && !d.in_warehouse && locations.some(l => l.is_warehouse) && (
            <ActionGroup label="Đổi máy — Trả về kho">
              <select className="assign-select" value={khoId} onChange={e => setKhoId(e.target.value)}>
                <option value="">— Chọn kho —</option>
                {locations.filter(l => l.is_warehouse).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <ActBtn danger loading={khoBusy} loadingText="⏳…" disabled={!khoId} onClick={returnToKhoNow}>📦 Trả về kho</ActBtn>
              <div style={{ flexBasis: '100%', fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                Khách <b>đổi máy</b>: lưu hồ sơ hiện tại vào <b>Chủ trước</b> → xoá data sống → chuyển máy về kho (sẵn sàng cho khách mới).
              </div>
            </ActionGroup>
          )}
          {isOps && (
            <ActionGroup label="Nguy hiểm">
              <ActBtn danger loading={clearBusy} loadingText="⏳ Đang xử lý…"
                title="Gỡ máy khỏi OD Travel MDM (RemoveProfile). GIỮ NGUYÊN dữ liệu khách. Dùng khi trả máy cho khách. KHÔNG tự gỡ Activation Lock — mở khoá bằng cách nhập tay mã bypass ở màn Activation Lock."
                onClick={() => setConfirm({ action: 'clear', step: 1 })}>🔓 Gỡ MDM (giữ dữ liệu)</ActBtn>
              <div style={{ flexBasis: '100%', fontSize: 12.5, color: 'var(--text2)', marginTop: 4, lineHeight: 1.6, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 11px' }}>
                <div>🔓 <b>Gỡ MDM (giữ dữ liệu)</b>: gỡ máy khỏi quản lý MDM, <b style={{ color: 'var(--green)' }}>GIỮ NGUYÊN dữ liệu</b> khách. → <i>Trả máy cho khách (đã hoàn tất hợp đồng).</i></div>
                <div style={{ marginTop: 5, color: 'var(--text3)' }}>⚠️ Máy thuộc <b>ABM</b>: sau khi gỡ vẫn còn trong ABM nên có thể tự enroll lại. Muốn giao hẳn cho khách phải <b>Release trong ABM</b>.</div>
              </div>
              {clearResult && clearResult.steps && (
                <div style={{ flexBasis: '100%', marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {clearResult.steps.map((st, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px' }}>
                      <span>{st.ok ? '✅' : '❌'}</span>
                      <div style={{ minWidth: 0 }}>
                        <b style={{ fontSize: 13 }}>{st.label}</b>
                        <div style={{ fontSize: 12, color: 'var(--text3)', wordBreak: 'break-word' }}>{st.detail}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ActionGroup>
          )}
          </>
          )}
        </div>

        {/* Tabs — "Chủ trước" (lịch sử đời enroll cũ) chỉ manager+ mới thấy */}
        <div className="modal-tabs">
          {TABS.filter(t => (t !== 'Chủ trước' || canManage) && !(inKho && ['Khách hàng', 'Thanh toán', 'Vị trí'].includes(t))).map(t => (
            <div
              key={t}
              className={`modal-tab${tab === t ? ' active' : ''}`}
              onClick={() => setTab(t)}
            >{t}</div>
          ))}
        </div>

        {/* Tab bodies */}
        <div className="tab-body active">
          {tab === 'Thông tin'    && <TabInfo device={device} liveInfo={info} onRefresh={refresh} loading={detailLoading} canManage={canManage} mdmGone={mdmGone} />}
          {tab === 'Vị trí'       && <TabGeo udid={device.udid} supervised={d.is_supervised} lostMode={d.lost_mode} onAction={onAction} canManage={canManage} mdmGone={mdmGone} />}
          {tab === 'Khách hàng'   && <TabOwner udid={device.udid} owner={owner} dueStatus={device.due_status} defaultEnroll={enrollYMD(device.created_at)} showToast={showToast} role={role} rentalAllowed={rentalAllowed} onSaved={o => { setOwner(o); onReload && onReload(); showToast && showToast('Đã lưu thông tin khách hàng', 'success', '💾'); }} />}
          {tab === 'Thanh toán'   && <TabPayments udid={device.udid} owner={owner} dueStatus={device.due_status} onLock={lostModeOn} supervised={d.is_supervised} canManage={canManage} canDelete={isOps} showToast={showToast} mdmGone={mdmGone} finalized={d.finalized} terminationProfit={d.termination_profit} terminationScenario={d.termination_scenario} terminationAmount={d.termination_amount} terminationNote={d.termination_note} terminatedAt={d.finalized_at} onTerminated={() => { onReload && onReload(); onClose && onClose(); }} />}
          {tab === 'Ứng dụng'    && <TabApps apps={apps} loading={detailLoading} onRefresh={refresh} udid={device.udid} onAction={onAction} canManage={canManage} mdmGone={mdmGone} />}
          {tab === 'Profiles'     && <TabProfiles profiles={profiles} loading={detailLoading} onRefresh={refresh} onChanged={reloadProfilesSoon} udid={device.udid} onAction={onAction} canManage={canManage} canRemove={isOps} mdmGone={mdmGone} />}
          {tab === 'Lịch sử lệnh' && <TabCommands commands={commands} loading={detailLoading} onRefresh={refresh} />}
          {tab === 'Nhật ký'      && <TabDeviceAudit udid={device.udid} />}
          {tab === 'Chủ trước'    && canManage && <TabHistory udid={device.udid} />}
        </div>
      </div>

      {/* Xác nhận 2 bước trước lệnh nguy hiểm (Gỡ MDM) */}
      {confirm && (
        <ConfirmDanger
          confirm={confirm}
          device={device}
          onCancel={() => setConfirm(null)}
          onNext={() => setConfirm(c => ({ ...c, step: 2 }))}
          onDo={() => {
            const a = confirm.action;
            setConfirm(null);
            if (a === 'clear') clearOrglock();
          }}
        />
      )}

      {/* Gửi thông báo lên màn hình máy này (đặt ảnh nền) */}
      {notifyOpen && (
        <NotifyModal device={device} onClose={() => setNotifyOpen(false)} showToast={showToast} />
      )}

      <style>{MODAL_CSS}</style>
      <style>{IMAGE_CSS}</style>
    </div>
  );
}

// ── Info Tab ───────────────────────────────────────────────────────────────────
function TabInfo({ device: d, liveInfo, onRefresh, loading, canManage, mdmGone }) {
  return (
    <div>
      <div className="tab-toolbar">
        <span className="tab-hint">Thông tin nhận dạng lấy từ lần enroll; số liệu động cập nhật khi thiết bị báo cáo.</span>
        {canManage && !mdmGone && <button className="btn" onClick={onRefresh} disabled={loading}>🔄 {loading ? 'Đang tải...' : 'Yêu cầu cập nhật'}</button>}
      </div>
      <InfoSection title="Nhận dạng thiết bị">
        <div className="info-grid g5">
          <InfoCell label="Tên thiết bị"  value={d.name} />
          <InfoCell label="Số serial"     value={d.serial} mono />
          <InfoCell label="UDID"          value={d.udid}   mono small />
          <InfoCell label="Model"         value={d.model} />
          <InfoCell label="Nền tảng"      value={d.platform} />
        </div>
      </InfoSection>
      <InfoSection title="Quản lý">
        <div className="info-grid g2">
          <InfoCell label="Cửa hàng"     value={d.location_name || 'Chưa gán'} />
          <InfoCell label="Người cài đặt" value={d.enrolled_by || '—'} />
        </div>
      </InfoSection>
      <InfoSection title="Hệ điều hành">
        <div className="info-grid g3">
          <InfoCell label="Phiên bản OS" value={d.os}      mono />
          <InfoCell label="Build"        value={d.build}   mono />
          <InfoCell label="Tên sản phẩm" value={d.product} />
        </div>
      </InfoSection>
      <InfoSection title="Phần cứng & kết nối">
        <div className="info-grid g4">
          <InfoCell label="Mức pin"      value={d.battery ? `${d.battery}%` : '—'} />
          <InfoCell label="Dung lượng"   value={d.storage || '—'} />
          <InfoCell label="MAC WiFi"     value={d.mac || '—'} mono />
          <InfoCell label="MAC BT"       value={d.bt || '—'}  mono />
        </div>
      </InfoSection>
      {liveInfo && Object.keys(liveInfo).length > 0 && (
        <InfoSection title="Dữ liệu báo cáo gần nhất (DeviceInformation)">
          <div className="info-grid g3">
            {/* Ẩn IsActivationLockEnabled: Apple deprecated từ iOS 16, luôn báo sai
                (thường "Không" dù Find My/AL đang bật). Trạng thái AL tin cậy xem ở
                nhóm "Activation Lock" (có mã bypass = AL đang bật). */}
            {Object.entries(liveInfo).filter(([k]) => k !== 'IsActivationLockEnabled').slice(0, 12).map(([k, v]) => (
              <InfoCell key={k} label={k} value={formatVal(v)} mono />
            ))}
          </div>
        </InfoSection>
      )}
    </div>
  );
}

// ── Vị trí Tab (định vị GPS qua DeviceLocation) ─────────────────────────────────
function TabGeo({ udid, supervised, lostMode, onAction, canManage, mdmGone }) {
  const [geo, setGeo]   = useState(undefined); // undefined = đang tải; null = chưa có
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const sup = supervised === false; // chắc chắn CHƯA giám sát -> không định vị được

  const load = () => query.getDeviceGeo(udid).then(setGeo).catch(() => setGeo(null));
  useEffect(() => { load(); return () => timer.current && clearInterval(timer.current); }, [udid]);

  async function locate() {
    setBusy(true);
    const prev = (geo && geo.updated_at) || '';
    const r = await onAction('locate', udid); // App đã toast; kết quả về sau
    if (r && r.ok === false) { setBusy(false); return; }
    // Chờ máy báo toạ độ về: poll tối đa ~45s, dừng khi có kết quả MỚI.
    let tries = 0;
    timer.current && clearInterval(timer.current);
    timer.current = setInterval(async () => {
      tries++;
      const g = await query.getDeviceGeo(udid).catch(() => null);
      if (g && g.updated_at && g.updated_at !== prev) {
        setGeo(g); clearInterval(timer.current); setBusy(false);
      } else if (tries >= 9) {
        clearInterval(timer.current); setBusy(false);
      }
    }, 5000);
  }

  return (
    <div>
      <div className="tab-toolbar">
        <span className="tab-hint">Vị trí GPS gần nhất (lệnh DeviceLocation). Chỉ chạy khi máy <b>giám sát</b> và đang <b>bị khóa</b> (Khóa điện thoại).</span>
        {canManage && (
          <button className="btn primary" disabled={sup || busy || mdmGone} title={mdmGone ? 'Máy đã gỡ MDM' : (sup ? SUP_HINT : '')} onClick={locate}>
            {busy ? '⏳ Đang định vị…' : '📍 Định vị ngay'}
          </button>
        )}
      </div>

      {sup && <div className="sup-warn" style={{ marginBottom: 12 }}>⚠️ Máy <b>chưa giám sát</b> — Apple không cho định vị. Cần enroll qua DEP/ABM.</div>}
      {!sup && lostMode !== true && (
        <div className="inst-warn" style={{ marginBottom: 12 }}>
          <span>⚠️ Máy hiện <b>KHÔNG</b> bị khóa. Apple chỉ trả toạ độ khi máy đang bị khóa — hãy <b>bật Khóa điện thoại</b> trước rồi bấm Định vị.</span>
        </div>
      )}

      {geo === undefined ? (
        <p style={{ color: 'var(--text3)', fontSize: 13 }}>Đang tải…</p>
      ) : geo === null ? (
        <EmptyState icon="📍" text="Chưa có dữ liệu vị trí"
          sub="Bấm “Định vị ngay” để yêu cầu; toạ độ hiện sau khi máy báo về (máy phải đang bị khóa)." />
      ) : geo.available ? (
        <GeoCard geo={geo} />
      ) : (
        <div className="inst-warn">
          <span>{geo.message || 'Lần định vị gần nhất chưa có toạ độ.'}{geo.updated_at ? ` (lúc ${dmyTime(geo.updated_at)})` : ''}</span>
        </div>
      )}
    </div>
  );
}

function GeoCard({ geo }) {
  const { latitude: lat, longitude: lng } = geo;
  const maps  = `https://www.google.com/maps?q=${lat},${lng}`;
  // Nhúng bản đồ Google trực tiếp (output=embed: không cần API key).
  const embed = `https://maps.google.com/maps?q=${lat},${lng}&z=16&hl=vi&output=embed`;
  return (
    <div>
      <InfoSection title="Vị trí gần nhất">
        <iframe
          title="Bản đồ vị trí thiết bị"
          className="geo-map"
          src={embed}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
        <div className="info-grid g2" style={{ marginTop: 10 }}>
          <InfoCell label="Vĩ độ (lat)"   value={String(lat)} mono />
          <InfoCell label="Kinh độ (lng)"  value={String(lng)} mono />
          <InfoCell label="Sai số"         value={geo.horizontal_accuracy != null ? `±${Math.round(geo.horizontal_accuracy)} m` : '—'} />
          <InfoCell label="Máy báo lúc"    value={geo.updated_at ? dmyTime(geo.updated_at) : '—'} />
        </div>
      </InfoSection>
      <a className="act-btn primary-act" style={{ textDecoration: 'none', width: '100%' }}
        href={maps} target="_blank" rel="noreferrer">🗺 Mở Google Maps (toàn màn hình / chỉ đường)</a>
    </div>
  );
}

// ── Apps Tab ────────────────────────────────────────────────────────────────────
function TabApps({ apps, loading, onRefresh, udid, onAction, canManage, mdmGone }) {
  return (
    <div>
      <div className="tab-toolbar">
        <span className="tab-hint">{apps.length} ứng dụng · dữ liệu từ InstalledApplicationList gần nhất</span>
        {canManage && !mdmGone && <button className="btn" onClick={onRefresh} disabled={loading}>🔄 {loading ? 'Đang tải...' : 'Yêu cầu danh sách'}</button>}
      </div>
      {apps.length === 0 ? (
        <EmptyState
          icon="📦"
          text="Chưa có dữ liệu ứng dụng"
          sub="Bấm “Yêu cầu danh sách” để gửi lệnh InstalledApplicationList; kết quả hiện sau khi thiết bị báo cáo."
        />
      ) : apps.map((a, i) => {
        const name = a.Name || a.name || a.Identifier || a.identifier || '(không tên)';
        const id   = a.Identifier || a.identifier || '';
        const ver  = a.ShortVersion || a.Version || a.version || '';
        return (
          <div className="list-row" key={i}>
            <div style={{ minWidth: 0 }}>
              <div className="list-row-title">{name}</div>
              <div className="list-row-sub">{id}{ver ? ` · v${ver}` : ''}</div>
            </div>
            {id && canManage && !mdmGone && (
              <button className="act-btn danger-act" onClick={async () => {
                await onAction('remove_app', udid, { identifier: id }); onRefresh();
              }}>Gỡ</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Profiles Tab ───────────────────────────────────────────────────────────────
function TabProfiles({ profiles, loading, onRefresh, onChanged, udid, onAction, canManage, canRemove, mdmGone }) {
  const fileRef = useRef(null);

  async function onPickFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const base64 = await readFileBase64(file);
    await onAction('install_profile', udid, { profileBase64: base64 });
    onChanged(); // gán xong -> tự cập nhật danh sách
  }

  return (
    <div>
      <div className="tab-toolbar">
        <span className="tab-hint">{profiles.length} profile · dữ liệu từ ProfileList gần nhất</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {canManage && !mdmGone && <button className="btn" onClick={onRefresh} disabled={loading}>🔄 Yêu cầu danh sách</button>}
          {canManage && !mdmGone && <button className="btn primary" onClick={() => fileRef.current?.click()}>+ Cài từ file .mobileconfig</button>}
          <input ref={fileRef} type="file" accept=".mobileconfig,application/x-apple-aspen-config,text/xml" hidden onChange={onPickFile} />
        </div>
      </div>
      {profiles.length === 0 ? (
        <EmptyState
          icon="📋"
          text="Chưa có dữ liệu profile"
          sub="Bấm “Yêu cầu danh sách” để gửi lệnh ProfileList, hoặc cài profile mới từ file .mobileconfig."
        />
      ) : profiles.map((p, i) => {
        const name = p.PayloadDisplayName || p.name || '(không tên)';
        const id   = p.PayloadIdentifier || p.identifier || '';
        return (
          <div className="list-row" key={i}>
            <div style={{ minWidth: 0 }}>
              <div className="list-row-title">{name}</div>
              <div className="list-row-sub">{id}</div>
            </div>
            {id && canRemove && !mdmGone && (
              <button className="act-btn danger-act" onClick={async () => {
                await onAction('remove_profile', udid, { identifier: id }); onChanged(); // gỡ xong -> tự cập nhật
              }}>Gỡ</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Command history Tab ──────────────────────────────────────────────────────────
function TabCommands({ commands, loading, onRefresh }) {
  const statusCls = (s) => {
    const t = (s || '').toLowerCase();
    if (t === 'acknowledged') return 'log-ok';
    if (t === 'error' || t === 'commandformaterror') return 'log-warn';
    return 'log-info';
  };
  return (
    <div>
      <div className="tab-toolbar">
        <span className="tab-hint">{commands.length} lệnh gần nhất</span>
        <button className="btn" onClick={onRefresh} disabled={loading}>🔄 Làm mới</button>
      </div>
      {commands.length === 0 ? (
        <EmptyState icon="📜" text="Chưa có lịch sử lệnh" sub="Các lệnh MDM đã gửi tới thiết bị và kết quả sẽ hiển thị ở đây." />
      ) : commands.map((c, i) => (
        <div className="log-row" key={c.command_uuid || i}>
          <span className="log-time">{dmyTime(c.updated_at)}</span>
          <span style={{ flex: 1, minWidth: 0 }} className="cmd-type">{c.request_type || '(không rõ)'}</span>
          <span className={`log-level ${statusCls(c.status)}`}>{c.status || '—'}</span>
        </div>
      ))}
    </div>
  );
}

// ── Nhật ký thao tác của thiết bị này ─────────────────────────────────────────
function TabDeviceAudit({ udid }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { query.getDeviceAudit(udid).then(setItems).catch(() => {}).finally(() => setLoading(false)); }, [udid]);
  return (
    <div>
      <div className="tab-toolbar"><span className="tab-hint">Ai đã thao tác gì trên thiết bị này ({items.length})</span></div>
      {loading ? <p style={{ color: 'var(--text3)', fontSize: 13 }}>Đang tải…</p>
        : items.length === 0 ? <EmptyState icon="📋" text="Chưa có thao tác nào" sub="Mọi lệnh, khóa, gán cửa hàng, thanh toán… sẽ hiện ở đây." />
          : <AuditList items={items} />}
    </div>
  );
}

// ── Chủ trước (lịch sử các đời enroll cũ đã lưu khi máy enroll lại) ─────────────
function TabHistory({ udid }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    query.getDeviceHistory(udid).then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, [udid]);
  return (
    <div>
      <div className="tab-toolbar">
        <span className="tab-hint">Hồ sơ khách của các lần enroll TRƯỚC — lưu lại khi máy enroll lại ({items.length}). Chỉ để tra cứu, không sửa được.</span>
      </div>
      {loading ? <p style={{ color: 'var(--text3)', fontSize: 13 }}>Đang tải…</p>
        : items.length === 0 ? <EmptyState icon="🗂" text="Chưa có chủ trước" sub="Khi máy được enroll lại, hồ sơ khách cũ sẽ được lưu vào đây trước khi làm mới." />
          : items.map(h => <HistoryCard key={h.id} udid={udid} h={h} />)}
    </div>
  );
}

function HistoryCard({ udid, h }) {
  const s = h.snapshot || {};
  const o = s.owner || {};
  const payments = s.payments || [];
  const images = s.images || [];
  const [viewer, setViewer] = useState(null); // index ảnh đang xem (gallery/lightbox)
  const paid = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  return (
    <div className="info-section" style={{ borderLeft: '3px solid var(--amber, #d97706)', paddingLeft: 12 }}>
      <div className="info-section-title" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
        <span>👤 {o.full_name || '(không tên)'}{o.phone ? ` · ${o.phone}` : ''}</span>
        <span style={{ fontWeight: 400, color: 'var(--text3)', fontSize: 12 }}>
          Ngày enroll {h.enroll_at || '—'} · làm mới {h.archived_at || '—'}
        </span>
      </div>
      <div className="info-grid g2">
        {o.national_id && <InfoCell label="CCCD/CMND" value={o.national_id} mono />}
        {o.email && <InfoCell label="Email" value={o.email} />}
        {o.address && <InfoCell label="Địa chỉ" value={o.address} />}
        {s.store && <InfoCell label="Cửa hàng" value={s.store} />}
        {s.installer && <InfoCell label="Người cài đặt" value={s.installer} />}
        {o.enrolled_on && <InfoCell label="Ngày hợp đồng" value={o.enrolled_on} />}
        {o.total_amount != null && <InfoCell label="Tổng tiền" value={money(o.total_amount)} />}
        {o.down_payment != null && <InfoCell label="Trả trước" value={money(o.down_payment)} />}
        {o.monthly_amount != null && <InfoCell label="Thanh toán/tháng" value={money(o.monthly_amount)} />}
        {o.installment_months != null && <InfoCell label="Số kỳ" value={String(o.installment_months)} />}
      </div>
      {Array.isArray(o.relatives) && o.relatives.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div className="info-cell-label" style={{ marginBottom: 4 }}>Người thân ({o.relatives.length})</div>
          <div className="info-grid g2">
            {o.relatives.map((r, i) => (
              <InfoCell key={i} label={r.relation || 'Người thân'} value={`${r.name || '—'}${r.phone ? ' · ' + r.phone : ''}`} />
            ))}
          </div>
        </div>
      )}
      {payments.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div className="info-cell-label" style={{ marginBottom: 4 }}>Thanh toán đã thu: <b>{money(paid)}</b> ({payments.length} lần)</div>
          <div className="tbl-wrap">
            <table>
              <thead><tr><th>Ngày</th><th>Số tiền</th><th>Ghi chú</th><th>Người thu</th></tr></thead>
              <tbody>
                {payments.map((p, i) => (
                  <tr key={i}>
                    <td>{p.paid_on || '—'}</td>
                    <td>{money(p.amount)}</td>
                    <td>{p.note || '—'}</td>
                    <td>{p.created_by_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {images.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div className="info-cell-label" style={{ marginBottom: 4 }}>Ảnh ({images.length}) — bấm để xem lớn</div>
          <div className="img-gallery">
            {images.map((im, i) => (
              <div className="img-thumb" key={im.id} title={im.orig_name || im.kind} role="button" tabIndex={0}
                onClick={() => setViewer(i)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setViewer(i); } }}>
                <img src={query.imageURL(udid, im.id)} alt={im.kind} loading="lazy" />
              </div>
            ))}
          </div>
          {viewer !== null && (
            <Lightbox
              items={images.map(im => ({ src: query.imageURL(udid, im.id), name: im.orig_name || im.kind }))}
              index={Math.min(viewer, images.length - 1)}
              onClose={() => setViewer(null)}
              onIndex={setViewer}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ── Khách hàng & Hợp đồng ─────────────────────────────────────────────────────────
const OWNER_FIELDS = ['full_name','email','phone','national_id','gender','address',
  'enrolled_on','installment_day','installment_months','installment_end',
  'total_amount','down_payment','monthly_amount','lost_message',
  // Cho thuê máy (loại trừ với trả góp). rent_total/rent_per_period do server tính.
  'contract_mode','rent_amount','rent_periods','rent_unit','rent_doc_fee','rent_ship_fee',
  'rent_day','rent_end'];

// "DD/MM/YYYY HH:MM" (device.created_at = ngày enroll) -> "YYYY-MM-DD" cho DateInput.
function enrollYMD(created) {
  const m = String(created || '').match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
}
function toForm(o, defaultEnroll) {
  const f = {};
  OWNER_FIELDS.forEach(k => { f[k] = (o && o[k] != null) ? String(o[k]) : ''; });
  // Ngày enroll mặc định = ngày máy được thêm vào MDM (khi khách chưa nhập).
  if (!f.enrolled_on && defaultEnroll) f.enrolled_on = defaultEnroll;
  if (f.contract_mode !== 'rental') f.contract_mode = 'installment';
  if (!f.rent_unit) f.rent_unit = 'month';
  return f;
}
const numOrNull = (v) => { const n = Number(v); return v === '' || isNaN(n) ? null : n; };
const fmtVND = (v) => { const n = Number(v); return isNaN(n) || v === '' ? '' : n.toLocaleString('vi-VN') + ' ₫'; };
const money = (v) => { const n = Number(v); return isNaN(n) ? '—' : n.toLocaleString('vi-VN') + ' ₫'; };
function addMonths(dateStr, m) {
  const d = new Date(dateStr);
  if (isNaN(d) || !m) return '';
  d.setMonth(d.getMonth() + Number(m));
  return d.toISOString().slice(0, 10);
}
// rateMultiplier / calcMonthly / totalWithInterest: xem ../lib/installment (nguồn duy nhất).
// Ngày đến hạn của kỳ thứ n (kỳ đầu = enroll + 1 tháng, kẹp theo ngày trả). Khớp periodsElapsed.
function dueDateOf(o, n) {
  if (!o || !o.enrolled_on) return null;
  const s = new Date(o.enrolled_on);
  if (isNaN(s)) return null;
  const day = Number(o.installment_day) || 1;
  const due = new Date(s); due.setMonth(due.getMonth() + n);
  const last = new Date(due.getFullYear(), due.getMonth() + 1, 0).getDate();
  due.setDate(Math.min(day, last));
  return due;
}
// Tóm tắt thanh toán định kỳ THEO LỊCH hợp đồng (hệ thống không theo dõi thanh toán thực tế).
function summarize(o) {
  const total = Number(o && o.total_amount) || 0, monthly = Number(o && o.monthly_amount) || 0;
  if (!total || !monthly) return null;
  const down = Number(o.down_payment) || 0, term = Number(o.installment_months) || 0;
  let elapsed = 0;
  if (o.enrolled_on) {
    const s = new Date(o.enrolled_on), now = new Date();
    elapsed = (now.getFullYear() - s.getFullYear()) * 12 + (now.getMonth() - s.getMonth());
    if (elapsed < 0) elapsed = 0;
    if (term) elapsed = Math.min(elapsed, term);
  }
  const dueByNow = Math.min(total, down + monthly * elapsed);
  return { total, down, monthly, term, elapsed, dueByNow, remaining: Math.max(0, total - dueByNow) };
}

// Trường bắt buộc (trừ Email). Khớp validate ở backend (owner.go).
//   type: 'pos' = số > 0 · 'num' = số (cho phép 0) · 'day' = 1–31 · mặc định = chuỗi khác rỗng.
// Thông tin khách — luôn bắt buộc ở cả hai hình thức hợp đồng.
const OWNER_REQUIRED_BASE = [
  { k: 'full_name', label: 'Họ tên' },
  { k: 'gender', label: 'Giới tính' },
  { k: 'national_id', label: 'Căn cước công dân' },
  { k: 'phone', label: 'Số điện thoại' },
  { k: 'enrolled_on', label: 'Ngày enroll' },
  { k: 'address', label: 'Địa chỉ' },
];
// Trả góp (thanh toán định kỳ).
const OWNER_REQUIRED_INSTALLMENT = [
  { k: 'total_amount', label: 'Tổng số tiền', type: 'pos' },
  { k: 'down_payment', label: 'Trả trước', type: 'num' },
  { k: 'installment_months', label: 'Kỳ (số tháng)', type: 'pos' },
  { k: 'monthly_amount', label: 'Thanh toán / tháng', type: 'pos' },
  { k: 'installment_day', label: 'Ngày trả hàng tháng', type: 'day' },
  { k: 'installment_end', label: 'Ngày kết thúc hợp đồng' },
];
// Cho thuê máy. Kỳ theo TUẦN thì "ngày thu" là thứ trong tuần (1-7), theo THÁNG là 1-31.
const ownerRequiredRental = (unit) => [
  { k: 'rent_amount', label: 'Tiền thuê thiết bị', type: 'pos' },
  { k: 'rent_periods', label: 'Số kỳ thuê', type: 'pos' },
  { k: 'rent_unit', label: 'Đơn vị kỳ thuê' },
  { k: 'rent_doc_fee', label: 'Phí xử lý hồ sơ', type: 'num' },
  { k: 'rent_day', label: unit === 'week' ? 'Thứ thu tiền mỗi tuần' : 'Ngày thu tiền mỗi kỳ',
    type: unit === 'week' ? 'day7' : 'day' },
  { k: 'rent_end', label: 'Ngày kết thúc thuê' },
];
// Danh sách bắt buộc theo hình thức đang chọn.
const ownerRequired = (mode, unit) =>
  OWNER_REQUIRED_BASE.concat(mode === 'rental' ? ownerRequiredRental(unit) : OWNER_REQUIRED_INSTALLMENT);

function ownerFieldMissing(f, k, type) {
  const v = String(f[k] ?? '').trim();
  if (v === '') return true;
  const n = Number(v);
  if (type === 'pos') return !(n > 0);
  if (type === 'day') return !(n >= 1 && n <= 31);
  if (type === 'day7') return !(n >= 1 && n <= 7);
  if (type === 'num') return isNaN(n);
  return false;
}

function TabOwner({ udid, owner, dueStatus, onLock, onSaved, defaultEnroll, showToast, role, rentalAllowed = false }) {
  const [f, setF] = useState(() => toForm(owner, defaultEnroll));
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  // Người thân (bắt buộc >= 2): tên + SĐT + quan hệ. Mặc định 2 dòng trống.
  const emptyRel = () => ({ name: '', phone: '', relation: '' });
  const initRels = (o) => (o && Array.isArray(o.relatives) && o.relatives.length
    ? o.relatives.map(r => ({ name: r.name || '', phone: r.phone || '', relation: r.relation || '' }))
    : [emptyRel(), emptyRel()]);
  const [rels, setRels] = useState(() => initRels(owner));
  const relValid = (r) => r.name.trim() !== '' && r.phone.trim() !== '' && r.relation.trim() !== '';
  const relsOk = rels.filter(relValid).length >= 2;
  const setRel = (i, k, v) => setRels(p => p.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const removeRel = (i) => setRels(p => p.length <= 2 ? p : p.filter((_, idx) => idx !== i));
  // Nhân viên chỉ nhập LẦN ĐẦU: nếu hồ sơ đã có dữ liệu -> khóa (chỉ quản lý sửa).
  const ownerExists = !!(owner && (owner.full_name || owner.phone || owner.national_id || owner.total_amount));
  const lockedForStaff = role === 'staff' && ownerExists;
  // Hình thức hợp đồng đang chọn (cửa hàng không bật cho thuê thì luôn là trả góp).
  const mode = rentalAllowed && f.contract_mode === 'rental' ? 'rental' : 'installment';
  const invalid = (k) => {
    if (!showErrors) return false;
    const spec = ownerRequired(mode, f.rent_unit).find(s => s.k === k);
    return spec ? ownerFieldMissing(f, k, spec.type) : false;
  };
  useEffect(() => { setF(toForm(owner, defaultEnroll)); setRels(initRels(owner)); setShowErrors(false); }, [owner, defaultEnroll]);
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));
  const setD = (k) => (v) => setF(p => ({ ...p, [k]: v })); // cho DateInput (nhận value, không phải event)

  // Tự tính lại thanh toán/tháng mỗi khi tổng/trả trước/kỳ đổi (hệ số đổi theo kỳ).
  // Ngày hết hạn chỉ điền khi trống (không ghi đè nếu đã nhập tay).
  useEffect(() => {
    setF(p => {
      const next = { ...p };
      let changed = false;
      const m = calcMonthly(p);
      if (m && m !== p.monthly_amount) { next.monthly_amount = m; changed = true; }
      if ((p.installment_end === '' || p.installment_end == null) && p.enrolled_on && Number(p.installment_months) > 0) {
        const end = addMonths(p.enrolled_on, p.installment_months);
        if (end) { next.installment_end = end; changed = true; }
      }
      return changed ? next : p;
    });
  }, [f.total_amount, f.down_payment, f.installment_months, f.enrolled_on]);

  // Thuê: tự điền ngày kết thúc = enroll + số kỳ × (tuần|tháng), chỉ khi ô còn trống.
  useEffect(() => {
    if (mode !== 'rental') return;
    setF(p => {
      if (p.rent_end) return p;
      const end = rentEndDate(p.enrolled_on, p.rent_periods, p.rent_unit);
      return end ? { ...p, rent_end: end } : p;
    });
  }, [mode, f.enrolled_on, f.rent_periods, f.rent_unit]);

  async function save() {
    const missing = ownerRequired(mode, f.rent_unit).filter(s => ownerFieldMissing(f, s.k, s.type)).map(s => s.label);
    if (!relsOk) missing.push('Ít nhất 2 người thân (đủ tên + SĐT + quan hệ)');
    if (missing.length) {
      setShowErrors(true);
      showToast && showToast('Thiếu thông tin bắt buộc: ' + missing.join(', '), 'error', '❌');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        full_name: f.full_name, email: f.email, phone: f.phone, national_id: f.national_id,
        gender: f.gender, address: f.address, enrolled_on: f.enrolled_on,
        lost_message: f.lost_message,
        contract_mode: mode,
        relatives: rels.filter(relValid).map(r => ({ name: r.name.trim(), phone: r.phone.trim(), relation: r.relation.trim() })),
      };
      // Chỉ gửi nhóm field của hình thức đang chọn — server cũng tự xoá nhóm còn lại.
      if (mode === 'rental') {
        Object.assign(payload, {
          rent_amount: numOrNull(f.rent_amount), rent_periods: numOrNull(f.rent_periods),
          rent_unit: f.rent_unit, rent_doc_fee: numOrNull(f.rent_doc_fee),
          rent_ship_fee: numOrNull(f.rent_ship_fee), rent_day: numOrNull(f.rent_day),
          rent_end: f.rent_end,
        });
      } else {
        Object.assign(payload, {
          installment_end: f.installment_end,
          installment_day: numOrNull(f.installment_day), installment_months: numOrNull(f.installment_months),
          total_amount: numOrNull(f.total_amount), down_payment: numOrNull(f.down_payment),
          monthly_amount: numOrNull(f.monthly_amount),
        });
      }
      await query.saveDeviceOwner(udid, payload);
      onSaved && onSaved({ ...payload });
    } catch (ex) { alertVN((ex.body && ex.body.error) || ex.message, { title: 'Lỗi' }); }
    finally { setSaving(false); }
  }

  return (
    <fieldset disabled={lockedForStaff} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      {lockedForStaff && (
        <div className="inst-warn" style={{ marginBottom: 14 }}>
          <span>🔒 Thông tin khách hàng đã được nhập. <b>Nhân viên chỉ nhập lần đầu</b> — các thay đổi sau do <b>quản lý cửa hàng</b> thực hiện.</span>
        </div>
      )}
      {dueStatus === 'overdue' && (
        <div className="inst-warn" style={{ marginBottom: 14 }}>
          <span>⚠️ Khách đang <b>trễ hạn thanh toán</b>. Mở tab <b>Thanh toán</b> để xem số tiền còn thiếu và khóa máy nhắc hạn.</span>
        </div>
      )}
      {dueStatus === 'ended' && <div className="page-note" style={{ marginBottom: 14 }}>Hợp đồng thanh toán đã hết hạn.</div>}
      <InfoSection title="Khách hàng">
        <div className="owner-grid">
          <Fld label="Họ tên" required invalid={invalid('full_name')}><input value={f.full_name} onChange={set('full_name')} placeholder="Nguyễn Văn A" /></Fld>
          <Fld label="Giới tính" required invalid={invalid('gender')}>
            <select value={f.gender} onChange={set('gender')}>
              <option value="">—</option><option value="male">Nam</option><option value="female">Nữ</option><option value="other">Khác</option>
            </select>
          </Fld>
          <Fld label="Căn cước công dân" required invalid={invalid('national_id')}><input value={f.national_id} onChange={set('national_id')} inputMode="numeric" /></Fld>
          <Fld label="Số điện thoại" required invalid={invalid('phone')}><input value={f.phone} onChange={set('phone')} inputMode="tel" /></Fld>
          <Fld label="Email"><input value={f.email} onChange={set('email')} type="email" /></Fld>
          <Fld label="Ngày enroll" required invalid={invalid('enrolled_on')}><DateInput value={f.enrolled_on} onChange={setD('enrolled_on')} /></Fld>
          <Fld label="Địa chỉ" full required invalid={invalid('address')}><textarea rows={2} value={f.address} onChange={set('address')} /></Fld>
          <div style={{ gridColumn: '1 / -1' }}>
            <div className="owner-fld"><span>Hình ảnh (hợp đồng, CCCD, khách… — tối đa 10)</span></div>
            <div style={{ marginTop: 6 }}>
              <ImageGallery udid={udid} kind="owner" max={10} showToast={showToast} />
            </div>
          </div>
        </div>
      </InfoSection>

      <InfoSection title="Người thân (bắt buộc ≥ 2)">
        <div className="rel-list">
          {rels.map((r, i) => (
            <div key={i} className={`rel-row${showErrors && !relsOk && !relValid(r) ? ' rel-row-invalid' : ''}`}>
              <input placeholder="Họ tên" value={r.name} onChange={e => setRel(i, 'name', e.target.value)} />
              <div className="rel-line2">
                <input placeholder="Số điện thoại" inputMode="tel" value={r.phone} onChange={e => setRel(i, 'phone', e.target.value)} />
                <input placeholder="Quan hệ (bố / mẹ / anh…)" value={r.relation} onChange={e => setRel(i, 'relation', e.target.value)} />
                <button type="button" className="act-btn rel-del" disabled={rels.length <= 2} title={rels.length <= 2 ? 'Tối thiểu 2 người thân' : 'Xoá'} onClick={() => removeRel(i)}>✕</button>
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" className="act-btn" style={{ fontSize: 12, padding: '6px 10px' }} onClick={() => setRels(p => [...p, emptyRel()])}>+ Thêm người thân</button>
            {showErrors && !relsOk && <span style={{ color: 'var(--red)', fontSize: 12 }}>Cần ít nhất 2 người thân đủ tên + SĐT + quan hệ.</span>}
          </div>
        </div>
      </InfoSection>

      {/* Hai hình thức LOẠI TRỪ nhau. Chỉ cửa hàng được bật "cho thuê máy" mới thấy lựa chọn. */}
      {rentalAllowed && (
        <InfoSection title="Hình thức hợp đồng">
          <div className="mode-pick">
            {[
              { v: 'installment', t: 'Thanh toán định kỳ', s: 'Khách trả góp theo tháng' },
              { v: 'rental', t: 'Cho thuê máy', s: 'Thu tiền thuê theo tuần / tháng' },
            ].map(op => (
              <label key={op.v} className={`mode-opt${mode === op.v ? ' on' : ''}`}>
                <input type="radio" name="contract_mode" checked={mode === op.v}
                  onChange={() => setF(p => ({ ...p, contract_mode: op.v }))} />
                <span><b>{op.t}</b><i>{op.s}</i></span>
              </label>
            ))}
          </div>
        </InfoSection>
      )}

      {mode === 'rental' ? (
      <InfoSection title="Cho thuê máy">
        <div className="owner-grid">
          <Fld label="Tiền thuê thiết bị" required invalid={invalid('rent_amount')} hint={fmtVND(f.rent_amount)}>
            <input value={f.rent_amount} onChange={set('rent_amount')} type="number" inputMode="numeric" placeholder="VND" />
          </Fld>
          <Fld label="Số kỳ thuê" required invalid={invalid('rent_periods')}>
            <input value={f.rent_periods} onChange={set('rent_periods')} type="number" min={1} placeholder="VD: 6" />
          </Fld>
          <Fld label="Đơn vị kỳ thuê" required invalid={invalid('rent_unit')}>
            <select value={f.rent_unit} onChange={set('rent_unit')}>
              <option value="month">Tháng</option>
              <option value="week">Tuần</option>
            </select>
          </Fld>
          <Fld label="Phí xử lý hồ sơ" required invalid={invalid('rent_doc_fee')} hint={fmtVND(f.rent_doc_fee)}>
            <input value={f.rent_doc_fee} onChange={set('rent_doc_fee')} type="number" inputMode="numeric" placeholder="VND" />
          </Fld>
          <Fld label="Phí giao nhận thiết bị (nếu có)" hint={fmtVND(f.rent_ship_fee)}>
            <input value={f.rent_ship_fee} onChange={set('rent_ship_fee')} type="number" inputMode="numeric" placeholder="VND — để trống nếu không có" />
          </Fld>
          <Fld label={f.rent_unit === 'week' ? 'Thứ thu tiền mỗi tuần' : 'Ngày thu tiền mỗi kỳ'} required invalid={invalid('rent_day')}>
            {f.rent_unit === 'week' ? (
              <select value={f.rent_day} onChange={set('rent_day')}>
                <option value="">— Chọn thứ —</option>
                {WEEKDAYS.map(d => <option key={d.v} value={d.v}>{d.label}</option>)}
              </select>
            ) : (
              <input value={f.rent_day} onChange={set('rent_day')} type="number" min={1} max={31} placeholder="1–31" />
            )}
          </Fld>
          <Fld label="Ngày kết thúc thuê" required invalid={invalid('rent_end')}>
            <DateInput value={f.rent_end} onChange={setD('rent_end')} />
          </Fld>
          <div style={{ gridColumn: '1 / -1', marginTop: -4 }}>
            <button type="button" className="act-btn" style={{ flex: 'none', fontSize: 12, padding: '6px 10px' }}
              disabled={!f.enrolled_on || !f.rent_periods}
              onClick={() => setF(p => ({ ...p, rent_end: rentEndDate(p.enrolled_on, p.rent_periods, p.rent_unit) }))}>
              📅 Tính ngày kết thúc = enroll + {f.rent_periods || '?'} {UNIT_LABEL[f.rent_unit] || 'kỳ'}
            </button>
          </div>
          {/* Hai số này do SERVER tính khi lưu — ở đây chỉ xem trước. */}
          <div className="rent-calc" style={{ gridColumn: '1 / -1' }}>
            <div><span>Tổng phí dịch vụ</span><b>{rentTotal(f) == null ? '—' : money(rentTotal(f))}</b></div>
            <div><span>Số tiền thanh toán mỗi kỳ</span><b>{rentPerPeriod(f) == null ? '—' : money(rentPerPeriod(f))}</b></div>
          </div>
          <div className="page-note" style={{ gridColumn: '1 / -1', marginTop: 2, lineHeight: 1.5 }}>
            <b>Tiền thuê thiết bị</b> là tiền thuê của <b>cả hợp đồng</b> (không phải mỗi kỳ).<br />
            Tổng phí dịch vụ = tiền thuê thiết bị + phí hồ sơ + phí giao nhận.<br />
            Thanh toán mỗi kỳ = tổng phí dịch vụ ÷ số kỳ — hệ thống tự tính, không nhập tay.
          </div>
        </div>
      </InfoSection>
      ) : (
      <InfoSection title="Thanh toán định kỳ">
        <div className="owner-grid">
          <Fld label="Tổng số tiền" required invalid={invalid('total_amount')} hint={fmtVND(f.total_amount)}><input value={f.total_amount} onChange={set('total_amount')} type="number" inputMode="numeric" placeholder="VND" /></Fld>
          <Fld label="Trả trước" required invalid={invalid('down_payment')} hint={fmtVND(f.down_payment)}><input value={f.down_payment} onChange={set('down_payment')} type="number" inputMode="numeric" placeholder="VND" /></Fld>
          <Fld label="Kỳ (số tháng)" required invalid={invalid('installment_months')}>
            <select value={f.installment_months} onChange={set('installment_months')}>
              <option value="">— Chọn kỳ —</option>
              {Array.from({ length: 6 }, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>{m} tháng</option>
              ))}
            </select>
          </Fld>
          <Fld label="Thanh toán / tháng" required invalid={invalid('monthly_amount')} hint={fmtVND(f.monthly_amount)}><input value={f.monthly_amount} onChange={set('monthly_amount')} type="number" inputMode="numeric" placeholder="VND" /></Fld>
          <Fld label="Ngày trả hàng tháng" required invalid={invalid('installment_day')}><input value={f.installment_day} onChange={set('installment_day')} type="number" min={1} max={31} placeholder="1–31" /></Fld>
          <Fld label="Ngày kết thúc hợp đồng" required invalid={invalid('installment_end')}><DateInput value={f.installment_end} onChange={setD('installment_end')} /></Fld>
          <div style={{ gridColumn: '1 / -1', marginTop: -4, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button type="button" className="act-btn" style={{ flex: 'none', fontSize: 12, padding: '6px 10px' }}
              disabled={!(Number(f.total_amount) > 0 && Number(f.installment_months) > 0)}
              onClick={() => setF(p => ({ ...p, monthly_amount: calcMonthly(p) || p.monthly_amount }))}>
              🧮 Tính thanh toán/tháng
            </button>
            <button type="button" className="act-btn" style={{ flex: 'none', fontSize: 12, padding: '6px 10px' }}
              disabled={!f.enrolled_on || !f.installment_months}
              onClick={() => setF(p => ({ ...p, installment_end: addMonths(p.enrolled_on, p.installment_months) }))}>
              📅 Tính ngày hết hạn = enroll + {f.installment_months || '?'} tháng
            </button>
          </div>
          {f.enrolled_on && Number(f.monthly_amount) > 0 && (() => {
            const term = Number(f.installment_months) || 0;
            const nextN = periodsElapsed(f) + 1;
            const next = (!term || nextN <= term) ? dueDateOf(f, nextN) : null;
            return (
              <div className="page-note" style={{ gridColumn: '1 / -1', marginTop: 6, lineHeight: 1.5 }}>
                🗓 Kỳ trả đầu tiên: <b>{dmyDate(dueDateOf(f, 1))}</b>
                {next ? <> · Kỳ tới: <b>{dmyDate(next)}</b></> : <> · Đã qua tất cả các kỳ</>}
                <br />Ngày trả <b>{f.installment_day || '?'}</b> hàng tháng, bắt đầu 1 tháng sau ngày enroll.
              </div>
            );
          })()}
        </div>
      </InfoSection>
      )}

      <InfoSection title="Khóa điện thoại">
        <Fld label="Câu thông báo hiển thị khi khóa điện thoại" full>
          <textarea rows={2} value={f.lost_message} onChange={set('lost_message')} placeholder="Thiết bị này thuộc quyền quản lý của công ty…" />
        </Fld>
      </InfoSection>

      <button className="act-btn primary-act" style={{ flex: 'none', width: '100%' }} disabled={saving} onClick={save}>
        {saving ? 'Đang lưu…' : '💾 Lưu thông tin'}
      </button>
    </fieldset>
  );
}

function Fld({ label, children, hint, full, required, invalid }) {
  return (
    <label className={`owner-fld${invalid ? ' owner-fld-invalid' : ''}`} style={full ? { gridColumn: '1 / -1' } : undefined}>
      <span>{label}{required ? <b className="owner-req"> *</b> : null}{hint ? <em className="owner-hint"> · {hint}</em> : null}</span>
      {children}
    </label>
  );
}

// Số kỳ đã ĐẾN HẠN tính tới hôm nay (kỳ đầu = ngày enroll + 1 tháng), giới hạn theo số kỳ.
function periodsElapsed(o) {
  if (!o || !o.enrolled_on || !o.monthly_amount) return 0;
  const s = new Date(o.enrolled_on), now = new Date();
  if (isNaN(s)) return 0;
  const day = Number(o.installment_day) || 1;
  const term = Number(o.installment_months) || 240;
  let p = 0;
  for (let m = 1; m <= term; m++) {
    const due = new Date(s); due.setMonth(due.getMonth() + m);
    const last = new Date(due.getFullYear(), due.getMonth() + 1, 0).getDate();
    due.setDate(Math.min(day, last));
    if (now < due) break;
    p = m;
  }
  return p;
}

// ── Tab Thanh toán (định kỳ) ──────────────────────────────────────────────────
function TabPayments({ udid, owner, dueStatus, onLock, supervised, canManage, canDelete, showToast, mdmGone, finalized, terminationProfit, terminationScenario, terminationAmount, terminationNote, terminatedAt, onTerminated }) {
  const [list, setList] = useState([]);
  const [paidSum, setPaidSum] = useState(0);
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  // Ngưng hợp đồng
  const [termOpen, setTermOpen] = useState(false);
  const [termScen, setTermScen] = useState('keep'); // keep | return
  const [termAmt, setTermAmt] = useState('');
  const [termNote, setTermNote] = useState('');
  const [termBusy, setTermBusy] = useState(false);
  const [pendingImgs, setPendingImgs] = useState([]); // ảnh chọn trước, upload sau khi tạo khoản
  const [pickerKey, setPickerKey] = useState(0);       // reset PendingImagePicker sau khi thêm

  const load = () => query.getPayments(udid).then(r => { setList(r.payments || []); setPaidSum(r.paid_sum || 0); }).catch(() => {});
  useEffect(() => { load(); }, [udid]);

  const total = Number(owner && owner.total_amount) || 0;
  const down = Number(owner && owner.down_payment) || 0;
  const months = Number(owner && owner.installment_months) || 0;
  // Thanh toán/tháng TÍNH theo công thức (khớp backend), không đọc giá trị đã lưu.
  const monthly = Number(calcMonthly(owner)) || Number(owner && owner.monthly_amount) || 0;
  const hasPlan = total > 0 && monthly > 0;
  // Nguồn chân lý = thanh toán/tháng ĐÃ LƯU (khớp bảng danh sách + backend): tổng phải trả
  // = trả trước + monthly×số kỳ. KHÔNG suy lại từ hệ số để tránh lệch số giữa 2 nơi.
  const financed = (monthly > 0 && months > 0) ? monthly * months : Math.max(0, total - down);
  const totalDue = down + financed;
  const paidTotal = down + paidSum;
  const remaining = Math.max(0, totalDue - paidTotal);
  const periods = periodsElapsed(owner);
  const expectedByNow = down + monthly * periods;
  const shortfall = Math.max(0, expectedByNow - paidTotal);

  // ── CHO THUÊ MÁY: tóm tắt riêng (số liệu trả góp ở trên không áp dụng) ──
  // Thuê không có "trả trước" nên đã thu = tổng các khoản đã ghi nhận.
  const isRental = !!(owner && owner.contract_mode === 'rental');
  const rentDue = Number(owner && owner.rent_total) || rentTotal(owner || {}) || 0;
  const rentEach = Number(owner && owner.rent_per_period) || rentPerPeriod(owner || {}) || 0;
  const rentN = Number(owner && owner.rent_periods) || 0;
  const rentDone = rentPeriodsElapsed(owner);
  const rentLeft = Math.max(0, rentDue - paidSum);
  const rentShort = Math.max(0, rentEach * rentDone - paidSum); // thu thiếu so với lịch

  async function add() {
    const amt = Number(amount);
    if (!amt || amt <= 0) { showToast && showToast('Nhập số tiền hợp lệ', 'error', '❌'); return; }
    setBusy(true);
    try {
      const res = await query.addPayment(udid, { amount: amt, paid_on: paidOn, note: note.trim() });
      // Upload ảnh đã chọn trước (nếu có) — gắn vào khoản vừa tạo.
      const pid = res && res.id;
      if (pid && pendingImgs.length) {
        for (const im of pendingImgs) {
          try { await query.uploadImage(udid, { kind: 'payment', paymentId: pid, blob: im.blob, name: im.name }); }
          catch (ex) { showToast && showToast('Ảnh: ' + ((ex.body && ex.body.error) || ex.message), 'error', '❌'); }
        }
      }
      setAmount(''); setNote(''); setPendingImgs([]); setPickerKey(k => k + 1); load();
      showToast && showToast('Đã ghi nhận thanh toán', 'success', '💵');
    } catch (ex) { showToast && showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setBusy(false); }
  }
  async function del(pid) {
    if (!(await confirmVN('Xóa khoản thanh toán này?', { title: 'Xóa thanh toán', okText: 'Xóa', danger: true }))) return;
    try { await query.deletePayment(udid, pid); load(); showToast && showToast('Đã xóa', 'success', '🗑'); }
    catch (ex) { showToast && showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
  }

  // Ngưng hợp đồng: thực nhận = đã thu (trả trước + kỳ) + đóng thêm − hoàn; lời = max(0, thực nhận − giá gốc).
  const termAmtN = Number(termAmt) || 0;
  const termReceived = termScen === 'return' ? paidTotal - termAmtN : paidTotal + termAmtN;
  // Trả máy: máy thu hồi lại -> lời = tiền giữ ròng (đã thu − hoàn). Giữ máy: lời = vượt giá gốc.
  const termProfit = termScen === 'return' ? termReceived : Math.max(0, termReceived - total);
  const termProfitLabel = termScen === 'return' ? 'Thu ròng' : 'Tiền lời';
  async function terminate() {
    const body = { scenario: termScen, note: termNote.trim() };
    if (termScen === 'return') body.refund = termAmtN; else body.settle = termAmtN;
    if (!(await confirmVN(`Chốt ngưng hợp đồng? Máy sẽ chuyển vào "Lịch sử máy" (ẩn khỏi danh sách Thiết bị). ${termProfitLabel} ghi nhận: ${money(termProfit)}.`, { title: 'Ngưng hợp đồng?', okText: 'Chốt ngưng' }))) return;
    setTermBusy(true);
    try {
      const r = await query.terminateContract(udid, body);
      showToast && showToast(`Đã ngưng HĐ — ${termProfitLabel.toLowerCase()} ${money(r.profit ?? termProfit)}`, 'success', '⏹');
      onTerminated && onTerminated();
    } catch (ex) { showToast && showToast((ex.body && ex.body.error) || ex.message, 'error', '❌'); }
    finally { setTermBusy(false); }
  }

  return (
    <div>
      {shortfall > 0 && dueStatus === 'overdue' && (
        <div className="inst-warn">
          <span>⚠️ Khách đang <b>thiếu {money(shortfall)}</b> so với lịch thanh toán. Có thể bật Khóa điện thoại để nhắc/khóa.</span>
          {!mdmGone && (
            <button className="act-btn danger-act" style={{ flex: 'none' }} disabled={supervised === false}
              title={supervised === false ? SUP_HINT : ''} onClick={onLock}>📍 Khóa điện thoại</button>
          )}
        </div>
      )}
      {dueStatus === 'ended' && <div className="page-note" style={{ marginBottom: 14 }}>Hợp đồng thanh toán đã hết hạn.</div>}

      {isRental ? (
        <>
          <div className="inst-summary" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px,1fr))' }}>
            <div className="inst-cell"><span>Tổng phí dịch vụ</span><b>{money(rentDue)}</b></div>
            <div className="inst-cell"><span>Đã thu</span><b>{money(paidSum)}</b></div>
            <div className="inst-cell"><span>Còn lại</span><b className={rentLeft > 0 ? 'rem' : ''}>{money(rentLeft)}</b></div>
            <div className="inst-cell"><span>Kỳ đã đến hạn</span><b>{rentDone}{rentN ? ` / ${rentN}` : ''}</b></div>
          </div>
          <div className="page-note" style={{ marginBottom: 14, lineHeight: 1.6 }}>
            🔑 <b>Hợp đồng cho thuê</b> — tiền thuê thiết bị <b>{money(owner.rent_amount)}</b> ·
            <b>{rentN}</b> kỳ ({UNIT_LABEL[owner.rent_unit] || 'kỳ'}) · thu <b>{money(rentEach)}</b> mỗi kỳ
            {owner.rent_day ? (
              <> · thu vào <b>{owner.rent_unit === 'week' ? weekdayLabel(owner.rent_day) : `ngày ${owner.rent_day}`}</b></>
            ) : null}
            <br />Phí xử lý hồ sơ <b>{money(owner.rent_doc_fee)}</b>
            {owner.rent_ship_fee ? <> · Phí giao nhận <b>{money(owner.rent_ship_fee)}</b></> : null}
            {owner.rent_end ? <> · Kết thúc thuê <b>{dmyDate(owner.rent_end)}</b></> : null}
            {rentShort > 0 && (
              <><br /><span style={{ color: 'var(--red)' }}>⚠️ Thu thiếu so với lịch: <b>{money(rentShort)}</b></span></>
            )}
          </div>
        </>
      ) : hasPlan ? (
        <div className="inst-summary" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px,1fr))' }}>
          <div className="inst-cell"><span>Tổng phải trả</span><b>{money(totalDue)}</b></div>
          <div className="inst-cell"><span>Đã thu</span><b>{money(paidTotal)}</b></div>
          <div className="inst-cell"><span>Còn lại</span><b className={remaining > 0 ? 'rem' : ''}>{money(remaining)}</b></div>
          <div className="inst-cell"><span>Kỳ đã đến hạn</span><b>{periods}{owner.installment_months ? ` / ${owner.installment_months}` : ''}</b></div>
        </div>
      ) : (
        <div className="page-note" style={{ marginBottom: 14 }}>Chưa có hợp đồng thanh toán. Nhập ở tab <b>Khách hàng</b> (tổng tiền, trả trước, thanh toán/tháng…).</div>
      )}

      {/* Máy đã ngưng/ở Lịch sử máy: không cho ngưng nữa, chỉ hiện tóm tắt. */}
      {canManage && total > 0 && finalized && (
        <div className="page-note" style={{ margin: '4px 0 14px', lineHeight: 1.7 }}>
          <div>⏹ Hợp đồng đã <b>ngưng</b> — máy đang ở <b>Lịch sử máy</b>.</div>
          {terminationScenario && <div>Kịch bản: <b>{terminationScenario === 'return' ? 'Khách trả máy' : 'Khách giữ máy (tất toán)'}</b>{terminatedAt ? <> · lúc <b>{terminatedAt}</b></> : ''}</div>}
          {terminationAmount != null && <div>{terminationScenario === 'return' ? 'Đã hoàn khách' : 'Khách đóng thêm'}: <b>{money(terminationAmount)}</b></div>}
          {terminationProfit != null && <div>{terminationScenario === 'return' ? 'Thu ròng (giữ lại)' : 'Tiền lời'}: <b style={{ color: 'var(--green)' }}>{money(terminationProfit)}</b></div>}
          {terminationNote ? <div>Ghi chú: <b>{terminationNote}</b></div> : ''}
          <div style={{ color: 'var(--text3)', marginTop: 2 }}>Khôi phục ở trang <b>Lịch sử máy</b> nếu cần mở lại.</div>
        </div>
      )}
      {/* Ngưng hợp đồng -> tính tiền lời + đưa vào Lịch sử máy. manager+. */}
      {canManage && total > 0 && !finalized && (
        <div style={{ margin: '4px 0 14px' }}>
          {!termOpen ? (
            <button className="act-btn" onClick={() => setTermOpen(true)}>⏹ Ngưng hợp đồng</button>
          ) : (
            <div style={{ border: '1px solid var(--border)', borderRadius: 11, padding: 12, background: 'var(--bg3)' }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>⏹ Ngưng hợp đồng — tính tiền lời</div>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 8, fontSize: 13 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                  <input type="radio" name="termscen" checked={termScen === 'keep'} onChange={() => setTermScen('keep')} /> Khách giữ máy (tất toán)
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                  <input type="radio" name="termscen" checked={termScen === 'return'} onChange={() => setTermScen('return')} /> Khách trả máy
                </label>
              </div>
              <div className="pay-form" style={{ marginBottom: 8 }}>
                <input type="number" inputMode="numeric" placeholder={termScen === 'return' ? 'Hoàn lại khách (VND)' : 'Khách đóng thêm (VND)'} value={termAmt} onChange={e => setTermAmt(e.target.value)} />
                <input placeholder="Ghi chú (tuỳ chọn)" value={termNote} onChange={e => setTermNote(e.target.value)} />
              </div>
              <div className="inst-summary" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px,1fr))', marginBottom: 10 }}>
                <div className="inst-cell"><span>Đã thu</span><b>{money(paidTotal)}</b></div>
                <div className="inst-cell"><span>{termScen === 'return' ? 'Hoàn khách' : 'Đóng thêm'}</span><b>{money(termAmtN)}</b></div>
                <div className="inst-cell"><span>Thực nhận</span><b>{money(termReceived)}</b></div>
                <div className="inst-cell"><span>Giá bán gốc</span><b>{money(total)}</b></div>
                <div className="inst-cell"><span>{termProfitLabel}</span><b style={{ color: 'var(--green)' }}>{money(termProfit)}</b></div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="act-btn primary-act" style={{ flex: 'none' }} disabled={termBusy} onClick={terminate}>{termBusy ? 'Đang chốt…' : '✅ Chốt ngưng → Lịch sử máy'}</button>
                <button className="act-btn" style={{ flex: 'none' }} onClick={() => setTermOpen(false)}>Huỷ</button>
              </div>
              <div className="page-note" style={{ marginTop: 8 }}>Chốt xong máy vào <b>Lịch sử máy</b>, ẩn khỏi danh sách Thiết bị (dữ liệu khách vẫn xem lại được). <b>Lời</b> = phần vượt trên giá bán gốc.</div>
            </div>
          )}
        </div>
      )}

      <div className="pay-block">
        {canManage && (
          <>
            <InfoSection title="Ghi nhận thanh toán">
              <div className="pay-form">
                <input type="number" inputMode="numeric" placeholder="Số tiền (VND)" value={amount} onChange={e => setAmount(e.target.value)} />
                <DateInput value={paidOn} onChange={setPaidOn} />
                <input placeholder="Ghi chú (kỳ, hình thức…)" value={note} onChange={e => setNote(e.target.value)} />
                <button className="act-btn primary-act" style={{ flex: 'none' }} disabled={busy} onClick={add}>+ Thêm</button>
              </div>
              {amount && <div className="owner-hint" style={{ marginTop: 6 }}>{fmtVND(amount)}</div>}
              <div style={{ marginTop: 10 }}>
                <div className="owner-hint" style={{ color: 'var(--text3)', fontWeight: 500, marginBottom: 6 }}>Ảnh chứng từ (tối đa 3) — đính kèm khi bấm Thêm</div>
                <PendingImagePicker key={pickerKey} max={3} onChange={setPendingImgs} showToast={showToast} />
              </div>
            </InfoSection>

            <div className="pay-block-divider" />
          </>
        )}

        <InfoSection title={`Lịch sử thanh toán (${list.length})`}>
          {list.length === 0 ? (
            <EmptyState icon="💵" text="Chưa có khoản thanh toán nào" sub="Ghi nhận mỗi lần khách thanh toán ở trên." />
          ) : list.map(p => (
            <div key={p.id} style={{ marginBottom: 7 }}>
              <div className="list-row" style={{ marginBottom: 0 }}>
                <div style={{ minWidth: 0 }}>
                  <div className="list-row-title">{money(p.amount)}</div>
                  <div className="list-row-sub">{dmy(p.paid_on)}{p.note ? ` · ${p.note}` : ''}{p.created_by_name ? ` · ${p.created_by_name}` : ''}</div>
                </div>
                {canDelete && <button className="act-btn danger-act" style={{ flex: 'none' }} onClick={() => del(p.id)}>Xóa</button>}
              </div>
              <div style={{ padding: '8px 4px 2px' }}>
                <ImageGallery udid={udid} kind="payment" paymentId={p.id} max={3} showToast={showToast} compact />
              </div>
            </div>
          ))}
        </InfoSection>
      </div>
    </div>
  );
}

// ── Reusable pieces ─────────────────────────────────────────────────────────────
function EmptyState({ icon, text, sub }) {
  return (
    <div className="empty-state">
      <div style={{ fontSize: 35 }}>{icon}</div>
      <div style={{ fontWeight: 500, color: 'var(--text2)' }}>{text}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text3)', maxWidth: 360, textAlign: 'center' }}>{sub}</div>}
    </div>
  );
}

function InfoSection({ title, children }) {
  return (
    <div className="info-section">
      <div className="info-section-title">{title}</div>
      {children}
    </div>
  );
}

function InfoCell({ label, value, mono, small }) {
  return (
    <div className="info-cell">
      <div className="info-cell-label">{label}</div>
      <div className={`info-cell-val${mono ? ' mono' : ''}${small ? ' small' : ''}`}>{value}</div>
    </div>
  );
}

// Hộp xác nhận 2 BƯỚC cho lệnh nguy hiểm: bước 1 giải thích hậu quả,
// bước 2 xác nhận lần cuối kèm tên/serial máy để chắc chắn đúng thiết bị.
function ConfirmDanger({ confirm, device, onCancel, onNext, onDo }) {
  const TEXTS = {
    clear: {
      icon: '🔓',
      title1: 'Gỡ MDM (giữ dữ liệu)?',
      desc1: 'Gỡ máy khỏi OD Travel MDM (RemoveProfile). Dữ liệu trên máy được GIỮ NGUYÊN (không xóa). Máy hết bị quản lý — sẽ không khóa / định vị / gửi lệnh được nữa. Nếu máy đang KHÓA (Lost Mode), hệ thống TỰ tắt khóa trước rồi mới gỡ (tránh kẹt máy vừa gỡ vừa khóa).',
      title2: 'Xác nhận lần cuối',
      desc2: `Gỡ MDM cho "${device.name}" (${device.serial || device.udid})? (giữ nguyên dữ liệu)`,
      doLabel: '🔓 Gỡ MDM (giữ dữ liệu)',
    },
  };
  const t = TEXTS[confirm.action];
  const first = confirm.step === 1;
  const [busy, setBusy] = useState(false);
  const doIt = async () => {
    setBusy(true);
    try { await onDo(); } finally { setBusy(false); }
  };
  return (
    <div className="confirm-overlay open">
      <div className="confirm-box">
        <div className="c-icon">{t.icon}</div>
        <div className="c-title">{first ? t.title1 : t.title2}</div>
        <div className="c-desc">{first ? t.desc1 : t.desc2}</div>
        <div className="c-btns">
          <button className="btn" onClick={onCancel} disabled={busy}>Hủy</button>
          {first
            ? <button className="btn danger" onClick={onNext}>Tiếp tục</button>
            : <button className="btn danger" onClick={doIt} disabled={busy}>{busy ? '⏳ Đang gửi…' : t.doLabel}</button>}
        </div>
      </div>
    </div>
  );
}

function ActionGroup({ label, children }) {
  return (
    <div className="action-group">
      <span className="action-group-label">{label}</span>
      {children}
    </div>
  );
}

function ActBtn({ children, onClick, primary, danger, disabled, title, loading, loadingText }) {
  const off = disabled || loading;
  const cls = `act-btn${primary ? ' primary-act' : ''}${danger ? ' danger-act' : ''}${off ? ' act-disabled' : ''}`;
  return (
    <button className={cls} onClick={off ? undefined : onClick} disabled={off} title={title}>
      {loading ? (loadingText || '⏳ Đang gửi…') : children}
    </button>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────────
function readFileBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function formatVal(v) {
  if (v == null) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  if (typeof v === 'boolean') return v ? 'Có' : 'Không';
  return String(v);
}

// ── Modal CSS ──────────────────────────────────────────────────────────────────
const MODAL_CSS = `
/* Drawer trượt từ PHẢI. Scope dưới .dev-drawer để LUÔN thắng .overlay/.modal của GLOBAL_CSS. */
@keyframes mSlideIn{from{transform:translateX(100%)}to{transform:translateX(0)}}
@keyframes mFadeIn{from{opacity:0}to{opacity:1}}
@keyframes mScaleIn{from{opacity:0;transform:scale(.96)}to{opacity:1;transform:scale(1)}}
.overlay.dev-drawer{position:fixed;inset:0;background:rgba(18,21,43,.34);z-index:100;display:flex;justify-content:flex-end;align-items:stretch;padding:0;overflow:hidden;animation:mFadeIn .2s ease}
.dev-drawer .modal{background:var(--bg2);border:none;border-left:1px solid var(--border);border-radius:0;width:clamp(600px,64vw,1240px);max-width:100%;height:100vh;flex-shrink:0;display:flex;flex-direction:column;overflow-y:auto;box-shadow:-16px 0 44px -18px rgba(18,21,43,.4);animation:mSlideIn .3s cubic-bezier(.2,.8,.2,1)}
.dev-drawer .modal-head{display:flex;align-items:center;justify-content:space-between;padding:18px 20px;border-bottom:1px solid var(--border);flex:none}
.dev-drawer .modal-title-wrap{display:flex;align-items:center;gap:12px;min-width:0}
.dev-drawer .modal-icon{width:44px;height:44px;background:var(--blue-bg);border:1px solid var(--blue-border);border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:23px;flex-shrink:0}
.dev-drawer .modal-device-name{font-weight:600;font-size:17px;font-family:var(--head)}.dev-drawer .modal-device-sub{font-size:12px;color:var(--text3);margin-top:2px}
.dev-drawer .modal-close{width:34px;height:34px;border:none;border-radius:10px;display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--text2);font-size:19px;background:var(--bg4);transition:all .15s;flex-shrink:0}
.dev-drawer .modal-close:hover{background:var(--border);color:var(--text)}
.dev-drawer .assign-bar{padding:12px 20px;border-bottom:1px solid var(--border);display:flex;align-items:center;gap:10px}
.dev-drawer .assign-select{flex:1;background:var(--bg3);border:1px solid var(--border);border-radius:9px;padding:9px 11px;color:var(--text);font-size:14px;font-family:inherit;outline:none}
.dev-drawer .assign-select:focus{border-color:var(--blue);box-shadow:0 0 0 3px var(--blue-bg)}

/* Nút hành động: giãn rộng, ~2 nút / hàng, to & dễ chạm */
.dev-drawer .action-bar{padding:16px 20px;border-bottom:1px solid var(--border);display:flex;flex-direction:column;gap:16px}
.dev-drawer .sup-warn{background:var(--amber-bg);color:#8a5a00;border:1px solid rgba(245,165,36,.4);border-radius:11px;padding:11px 13px;font-size:13px;line-height:1.5}
.dev-drawer .lost-banner{background:var(--red-bg);color:#c02b3a;border:1px solid var(--red-border);border-radius:11px;padding:11px 13px;font-size:13px;line-height:1.5}
.dev-drawer .action-group{display:flex;flex-wrap:wrap;gap:8px}
.dev-drawer .action-group-label{flex:1 1 100%;font-size:11px;font-weight:700;color:var(--text3);text-transform:uppercase;letter-spacing:.07em;margin-bottom:3px}
.dev-drawer .act-btn{flex:1 1 140px;display:inline-flex;align-items:center;justify-content:center;gap:7px;padding:12px 14px;font-size:13.5px;font-weight:600;font-family:inherit;border:1px solid var(--border);border-radius:11px;cursor:pointer;background:#fff;color:var(--text);transition:all .16s cubic-bezier(.4,0,.2,1);white-space:nowrap}
.dev-drawer .act-btn:hover{border-color:var(--blue);background:var(--blue-bg);color:var(--blue);transform:translateY(-1px);box-shadow:var(--shadow)}
.dev-drawer .act-btn.primary-act{background:var(--blue);color:#fff;border-color:var(--blue)}
.dev-drawer .act-btn.primary-act:hover{background:var(--blue);color:#fff;box-shadow:0 8px 18px -6px rgba(79,110,247,.6)}
.dev-drawer .act-btn.danger-act{background:var(--red-bg);color:var(--red);border-color:var(--red-border)}
.dev-drawer .act-btn.danger-act:hover{background:var(--red);color:#fff;border-color:var(--red)}
.dev-drawer .act-btn.act-disabled{opacity:.4;cursor:not-allowed;background:var(--bg3);color:var(--text3)}
.dev-drawer .act-btn.act-disabled:hover{border-color:var(--border);background:var(--bg3);color:var(--text3);transform:none;box-shadow:none}

.dev-drawer .modal-tabs{display:flex;flex:none;padding:0 20px;border-bottom:1px solid var(--border);overflow-x:auto;background:var(--bg3)}
.dev-drawer .modal-tab{padding:13px 15px;font-size:14px;font-weight:600;cursor:pointer;color:var(--text2);border-bottom:2px solid transparent;transition:all .18s;margin-bottom:-1px;white-space:nowrap}
.dev-drawer .modal-tab:hover{color:var(--text)}.dev-drawer .modal-tab.active{color:var(--blue);border-bottom-color:var(--blue)}
.dev-drawer .tab-body{padding:18px 20px;animation:mFadeIn .25s ease}
.dev-drawer .tab-toolbar{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:14px;flex-wrap:wrap}
.dev-drawer .tab-hint{font-size:12px;color:var(--text3)}
.dev-drawer .info-section{margin-bottom:18px}
.dev-drawer .info-section-title{font-size:11px;font-weight:700;color:var(--text3);text-transform:uppercase;letter-spacing:.06em;margin-bottom:10px}
.dev-drawer .info-grid{display:grid;gap:8px;grid-template-columns:repeat(2,1fr)}
.dev-drawer .info-cell{background:var(--bg3);border:1px solid var(--border);border-radius:var(--radius);padding:11px 13px;min-width:0;transition:border-color .16s}
.dev-drawer .info-cell:hover{border-color:var(--border2)}
.dev-drawer .info-cell-label{font-size:11px;color:var(--text3);font-weight:600;text-transform:uppercase;letter-spacing:.04em;margin-bottom:5px}
.dev-drawer .info-cell-val{font-size:14px;color:var(--text);font-weight:500;word-break:break-all}.dev-drawer .info-cell-val.mono{font-family:'JetBrains Mono',monospace;font-size:13px}.dev-drawer .info-cell-val.small{font-size:12px}
.dev-drawer .geo-map{width:100%;height:340px;border:1px solid var(--border);border-radius:var(--radius);display:block;background:var(--bg3)}
.dev-drawer .inst-warn{display:flex;align-items:center;gap:12px;flex-wrap:wrap;background:var(--amber-bg);color:#b07610;border:1px solid rgba(245,165,36,.35);border-radius:12px;padding:12px 14px;font-size:14px;margin-bottom:14px}
.dev-drawer .inst-warn span{flex:1;min-width:180px}
.dev-drawer .inst-summary{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:16px}
.dev-drawer .inst-cell{background:var(--bg3);border:1px solid var(--border);border-radius:var(--radius);padding:11px 13px}
.dev-drawer .inst-cell span{display:block;font-size:11px;font-weight:600;color:var(--text3);text-transform:uppercase;letter-spacing:.04em;margin-bottom:5px}
.dev-drawer .inst-cell b{font-size:16px;font-family:var(--head)}
.dev-drawer .inst-cell b.rem{color:var(--red)}
@media(max-width:560px){.dev-drawer .inst-summary{grid-template-columns:1fr}}
.dev-drawer .owner-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:4px}
/* Chọn hình thức hợp đồng: trả góp | cho thuê (loại trừ nhau) */
.dev-drawer .mode-pick{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.dev-drawer .mode-opt{display:flex;align-items:flex-start;gap:9px;padding:11px 13px;cursor:pointer;
  border:1px solid var(--border);border-radius:10px;background:var(--bg3);transition:border-color .15s,background .15s}
.dev-drawer .mode-opt.on{border-color:var(--blue);background:var(--blue-bg)}
.dev-drawer .mode-opt input{width:auto;margin-top:2px;flex:none}
.dev-drawer .mode-opt b{display:block;font-size:14px;color:var(--text)}
.dev-drawer .mode-opt i{display:block;font-style:normal;font-size:12px;color:var(--text3);margin-top:2px}
/* Hai số server tự tính của hợp đồng thuê */
.dev-drawer .rent-calc{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:4px}
.dev-drawer .rent-calc>div{background:var(--blue-bg);border:1px solid var(--blue-border);border-radius:10px;padding:10px 12px}
.dev-drawer .rent-calc span{display:block;font-size:12px;font-weight:600;color:var(--text2)}
.dev-drawer .rent-calc b{display:block;font-size:16px;color:var(--text);margin-top:3px}
@media(max-width:560px){.dev-drawer .mode-pick,.dev-drawer .rent-calc{grid-template-columns:1fr}}
.dev-drawer .owner-fld{display:flex;flex-direction:column;gap:5px;min-width:0}
.dev-drawer .owner-fld>span{font-size:12px;font-weight:600;color:var(--text2)}
.dev-drawer .owner-req{color:var(--red);font-weight:700}
.dev-drawer .owner-fld-invalid input,.dev-drawer .owner-fld-invalid select,.dev-drawer .owner-fld-invalid textarea{border-color:var(--red)!important;box-shadow:0 0 0 3px var(--red-bg)!important}
.dev-drawer .owner-hint{font-style:normal;color:var(--green);font-weight:600}
.dev-drawer .owner-fld input,.dev-drawer .owner-fld select,.dev-drawer .owner-fld textarea{background:var(--bg3);border:1px solid var(--border);border-radius:9px;padding:9px 11px;color:var(--text);font-size:14px;font-family:inherit;outline:none;width:100%;resize:vertical}
.dev-drawer .owner-fld input:focus,.dev-drawer .owner-fld select:focus,.dev-drawer .owner-fld textarea:focus{border-color:var(--blue);box-shadow:0 0 0 3px var(--blue-bg)}
@media(max-width:560px){.dev-drawer .owner-grid{grid-template-columns:1fr}}
.dev-drawer .rel-list{display:flex;flex-direction:column;gap:12px}
.dev-drawer .rel-row{display:flex;flex-direction:column;gap:6px}
.dev-drawer .rel-line2{display:flex;gap:6px;align-items:center}
.dev-drawer .rel-line2 input{flex:1 1 0;min-width:0}
.dev-drawer .rel-row input{width:100%;background:var(--bg3);border:1px solid var(--border);border-radius:9px;padding:9px 11px;color:var(--text);font-size:14px;font-family:inherit;outline:none}
.dev-drawer .rel-row input:focus{border-color:var(--blue);box-shadow:0 0 0 3px var(--blue-bg)}
.dev-drawer .rel-row-invalid input{border-color:var(--red)!important;box-shadow:0 0 0 3px var(--red-bg)!important}
.dev-drawer .rel-del{flex:none}
/* Gộp "Ghi nhận thanh toán" + "Lịch sử thanh toán" thành 1 khối viền chung */
.dev-drawer .pay-block{border:1px solid var(--border);border-radius:var(--radius);background:var(--bg2);padding:16px;margin-bottom:18px}
.dev-drawer .pay-block .info-section{margin-bottom:0}
.dev-drawer .pay-block-divider{height:1px;background:var(--border);margin:16px 0}
.dev-drawer .pay-form{display:flex;flex-wrap:wrap;gap:8px}
.dev-drawer .pay-form input{flex:1 1 120px;min-width:0;background:var(--bg3);border:1px solid var(--border);border-radius:9px;padding:10px 11px;color:var(--text);font-size:14px;font-family:inherit;outline:none}
.dev-drawer .pay-form input:focus{border-color:var(--blue);box-shadow:0 0 0 3px var(--blue-bg)}
/* DateInput: ô text dd/mm/yyyy + nút lịch native ở bên phải */
.dev-drawer .date-field{position:relative;display:block;width:100%}
.dev-drawer .pay-form .date-field{flex:1 1 120px;min-width:0}
.dev-drawer .date-field .date-text{width:100%;padding-right:38px}
.dev-drawer .date-field .date-pick{position:absolute;right:0;top:0;height:100%;width:40px;min-width:0;padding:0;border:none!important;background:transparent!important;color:transparent;box-shadow:none!important;cursor:pointer}
.dev-drawer .date-field .date-pick::-webkit-datetime-edit,.dev-drawer .date-field .date-pick::-webkit-inner-spin-button,.dev-drawer .date-field .date-pick::-webkit-clear-button{display:none}
.dev-drawer .date-field .date-pick::-webkit-calendar-picker-indicator{position:absolute;right:10px;top:50%;transform:translateY(-50%);margin:0;cursor:pointer;opacity:.5}
.dev-drawer .date-field .date-pick::-webkit-calendar-picker-indicator:hover{opacity:.85}
.dev-drawer .list-row{display:flex;align-items:center;justify-content:space-between;gap:10px;background:var(--bg3);border:1px solid var(--border);border-radius:var(--radius);padding:11px 14px;margin-bottom:7px;transition:border-color .16s,transform .16s}
.dev-drawer .list-row:hover{border-color:var(--border2);transform:translateX(2px)}
.dev-drawer .list-row-title{font-weight:600;font-size:14px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.dev-drawer .list-row-sub{font-size:12px;color:var(--text3);font-family:'JetBrains Mono',monospace;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.dev-drawer .empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;padding:40px 16px;text-align:center}
.dev-drawer .log-row{display:flex;align-items:center;gap:14px;padding:9px 12px;background:var(--bg3);border:1px solid var(--border);border-radius:var(--radius);margin-bottom:6px;font-size:13px}
.dev-drawer .log-time{font-family:'JetBrains Mono',monospace;font-size:12px;color:var(--text3);flex-shrink:0}
.dev-drawer .cmd-type{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:var(--text)}
.dev-drawer .log-level{font-size:11px;font-weight:700;padding:3px 7px;border-radius:5px;flex-shrink:0}
.dev-drawer .log-ok{background:var(--green-bg);color:#0a8e63}.dev-drawer .log-info{background:var(--blue-bg);color:var(--blue)}.dev-drawer .log-warn{background:var(--amber-bg);color:#b07610}
.dev-drawer .confirm-overlay{position:fixed;inset:0;background:rgba(18,21,43,.5);z-index:200;display:flex;align-items:center;justify-content:center;padding:20px;animation:mFadeIn .2s ease}
.dev-drawer .confirm-box{background:var(--bg2);border:1px solid var(--border);border-radius:16px;padding:28px;width:360px;max-width:100%;text-align:center;box-shadow:var(--shadow-lg);animation:mScaleIn .25s cubic-bezier(.2,.8,.2,1)}
.dev-drawer .c-icon{font-size:43px;margin-bottom:12px}.dev-drawer .c-title{font-weight:700;font-size:18px;margin-bottom:8px;font-family:var(--head)}.dev-drawer .c-desc{font-size:14px;color:var(--text2);margin-bottom:22px;line-height:1.6}
.dev-drawer .c-btns{display:flex;gap:10px;justify-content:center}

@media (max-width:768px){
.dev-drawer .modal{width:100%;border-left:none}
.dev-drawer .modal-head{padding:14px 16px}
.dev-drawer .action-bar,.dev-drawer .assign-bar,.dev-drawer .tab-body{padding-left:16px;padding-right:16px}
.dev-drawer .modal-tabs{padding:0 16px}
}
@media (max-width:420px){.dev-drawer .info-grid{grid-template-columns:1fr}}
/* Drawer rộng trên laptop -> lưới thông tin 3 cột cho thoáng */
@media (min-width:1200px){.dev-drawer .info-grid{grid-template-columns:repeat(3,1fr)}}
@media (prefers-reduced-motion:reduce){*,*::before,*::after{animation-duration:.001ms!important;transition-duration:.001ms!important}}
`;
